import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment,
  limit,
  setDoc
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { db } from './config';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { httpsCallable } from 'firebase/functions';
import { functions } from './config';
import { safeTimestampToISOString } from '../utils/dateUtils';
import mammoth from 'mammoth'; // Import mammoth for DOCX extraction
import * as pdfjsLib from 'pdfjs-dist'; // Import PDF.js for PDF extraction
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, TextRun } from 'docx';

// Import API keys
import { GEMINI_API_KEY, STRIPE_PUBLISHABLE_KEY, RAZORPAY_KEY_ID } from '../config/keys';

// Set worker source for PDF.js v4.x
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Initialize Gemini API
const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);

// Enhanced Excel processing with professional output - RESTORED FULL FUNCTIONALITY
export const processExcelWithGemini = async (file, jobType, preUpsells = null) => {
  // Declare variables at the top level so they're available in the catch block
  let csvData;
  let originalDataArray = [];
  let dataInfo = { rows: 0, columns: 0, headers: [] };
  let fileType = file ? file.name.split('.').pop().toLowerCase() : 'csv';

  try {
    console.log(`Processing Excel file with Gemini: ${file.name}, job type: ${jobType}`);

    // Import XLSX library dynamically for Excel processing
    const XLSX = await import('xlsx').then(module => module.default || module);

    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 10) {
      console.warn(`File size (${fileSizeInMB.toFixed(2)}MB) exceeds recommended limit for Gemini API`);
    }

    // Process file content based on type
    fileType = file.name.split('.').pop().toLowerCase();

    if (fileType === 'csv') {
      // For CSV files, read as text directly
      const textContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      csvData = textContent;

      // Get basic info about the CSV
      const lines = textContent.split('\n');
      if (lines.length > 0) {
        // Parse the header row
        const headers = lines[0].split(',').map(col => col.trim().replace(/^"(.*)"$/, '$1'));

        // Parse data rows properly - handle quoted fields
        originalDataArray = [headers]; // First add headers
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue; // Skip empty lines

          // Better CSV parsing handling quoted fields with commas
          let row = [];
          let inQuote = false;
          let currentValue = "";

          for (let j = 0; j < lines[i].length; j++) {
            const char = lines[i][j];

            if (char === '"' && (j === 0 || lines[i][j - 1] !== '\\')) {
              inQuote = !inQuote;
            } else if (char === ',' && !inQuote) {
              // End of field
              row.push(currentValue.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
              currentValue = "";
            } else {
              currentValue += char;
            }
          }

          // Add the last field
          if (currentValue) {
            row.push(currentValue.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
          }

          originalDataArray.push(row);
        }

        dataInfo = {
          rows: originalDataArray.length - 1,
          columns: headers.length,
          headers: headers,
          sampleRows: originalDataArray.slice(1, Math.min(6, originalDataArray.length)) // First 5 data rows or fewer
        };
      }

      console.log('CSV data extracted successfully:', {
        rows: dataInfo.rows,
        columns: dataInfo.columns,
        headerCount: dataInfo.headers?.length
      });
    } else if (fileType === 'xlsx' || fileType === 'xls') {
      // For Excel files, convert to CSV using XLSX library
      console.log('Converting Excel file to CSV...');

      // Read as ArrayBuffer
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsArrayBuffer(file);
      });

      // Parse with XLSX
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // Process all sheets in the workbook
      const allSheets = workbook.SheetNames;
      let combinedCsvData = '';
      let combinedJsonData = [];

      // Process each sheet
      for (let i = 0; i < allSheets.length; i++) {
        const sheetName = allSheets[i];
        const worksheet = workbook.Sheets[sheetName];

        // Convert current sheet to CSV
        const sheetCsvData = XLSX.utils.sheet_to_csv(worksheet);

        // Add sheet name as a separator if not the first sheet
        if (i > 0) {
          combinedCsvData += `\n\n### SHEET: ${sheetName} ###\n\n`;
        } else {
          combinedCsvData += `### SHEET: ${sheetName} ###\n\n`;
        }
        combinedCsvData += sheetCsvData;

        // Convert to JSON to easily access headers and rows
        const sheetJsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Add sheet data to combined data with sheet name marker
        if (i === 0) {
          // For first sheet, use as is
          combinedJsonData = sheetJsonData;
        } else {
          // For subsequent sheets, add a row with sheet name, then the data
          combinedJsonData.push([`### SHEET: ${sheetName} ###`]);
          combinedJsonData = combinedJsonData.concat(sheetJsonData);
        }
      }

      // Use combined data for processing
      csvData = combinedCsvData;
      originalDataArray = combinedJsonData; // Store full original data

      // Calculate data info from combined data
      if (combinedJsonData.length > 0) {
        // Count total rows across all sheets
        let totalRows = 0;
        let firstSheetHeaders = [];
        let firstSheetSampleRows = [];

        // Find the first actual data sheet with headers
        for (let i = 0; i < combinedJsonData.length; i++) {
          const row = combinedJsonData[i];
          // Skip sheet marker rows
          if (row.length === 1 && String(row[0]).includes('### SHEET:')) {
            continue;
          }

          // First non-marker row is considered the header of first sheet
          if (!firstSheetHeaders.length) {
            firstSheetHeaders = row;
            // Get sample rows from first sheet
            let sampleCount = 0;
            let j = i + 1;
            while (j < combinedJsonData.length && sampleCount < 5) {
              const sampleRow = combinedJsonData[j];
              // Stop if we hit another sheet marker
              if (sampleRow.length === 1 && String(sampleRow[0]).includes('### SHEET:')) {
                break;
              }
              firstSheetSampleRows.push(sampleRow);
              sampleCount++;
              j++;
            }
          }

          // Count as a data row if not a sheet marker
          if (!(row.length === 1 && String(row[0]).includes('### SHEET:'))) {
            totalRows++;
          }
        }

        // Subtract 1 for the header row of first sheet
        totalRows = totalRows > 0 ? totalRows - 1 : 0;

        dataInfo = {
          rows: totalRows,
          columns: firstSheetHeaders.length,
          headers: firstSheetHeaders,
          sampleRows: firstSheetSampleRows,
          sheetCount: allSheets.length
        };
      }

      console.log('Excel data converted to CSV successfully:', {
        rows: dataInfo.rows,
        columns: dataInfo.columns,
        headerCount: dataInfo.headers?.length
      });
    } else {
      throw new Error(`Unsupported file type: ${fileType}. Please upload a CSV or Excel file.`);
    }

    // Generate prompt based on job type
    let prompt = '';
    if (jobType === 'excel_formatting') {
      // Determine if this is CSV or Excel format
      const isCSV = file.name.endsWith('.csv');

      // Add information about multiple sheets to the prompt
      const hasMultipleSheets = !isCSV && dataInfo.sheetCount > 1;

      if (isCSV) {
        // Enhanced CSV prompt with styling and structure guidance
        prompt = `
        You are a data formatting specialist. Your task is to analyze this Excel/CSV data and provide a COMPLETE formatted version with significant improvements while preserving ALL original data. But you can change number and name of columns. If needed you can split columns into multiple columns.

        IMPORTANT RULES:
        1. NEVER remove or modify any actual data values
        2. NEVER skip any rows or columns, but you can add new columns if needed.
        3. ALWAYS return the complete dataset
        4. ALWAYS preserve numerical values exactly as they are
        5. ONLY improve formatting, headers, and structure

        Make these specific formatting improvements:
        1. Column Headers:
           - Convert to clear, professional titles
           - Use proper capitalization (Title Case)
           - Add units in parentheses where applicable
           - Make headers descriptive but concise

        2. Data Type Formatting:
           - Format numbers WITHOUT thousand separators
           - Percentages: Show % symbol and proper decimal places
           - Dates: Convert to consistent YYYY-MM-DD format
           - Currency: Add currency symbols and decimal places
           - Text: Proper capitalization for names/titles
        
        STYLING AND STRUCTURE REQUIREMENTS:
        1. Create a professional, visually appealing output with clear sections
        2. Use color coding for different data categories (e.g., metrics, growth rates)
        3. Bold key performance indicators and important insights
        4. Include a header section with summary metrics
        5. Organize data into logical sections with clear headings
        6. Add visual separators between sections
        7. Highlight positive trends in green, negative in red where appropriate
        8. Include strategic recommendations based on the data
        9. Structure the output similar to a professional dashboard
        
        CRITICAL OUTPUT REQUIREMENTS:
        0. IMPORTANT: At the VERY TOP of your response, include a plain-language section titled "# IMPROVEMENT SUMMARY" with 5-10 concise bullet points that describe what was improved (headers, types, formatting, styling, structure) for a non-technical reader. After this summary, follow the exact sections below unchanged.
        1. Output Format: Use ONLY the following format for your response:
           # FORMATTED DATA
           [The complete formatted data in clean CSV format]
           
           # COLUMN SPECIFICATIONS
           [Column-by-column formatting details]
           
           # FORMATTING CHANGES MADE
           [List of improvements made]
           
           # STYLING RECOMMENDATIONS
           [Specific styling recommendations for Excel including colors, bold text, borders, etc.]

        3. Data Integrity Rules:
           - Preserve ALL original data rows and columns exactly
           - Keep all unique identifiers (IDs) unchanged
           - Maintain data relationships and ordering
           - Never truncate or omit any data

        4. CSV Formatting Rules:
           - Use comma as delimiter
           - Properly escape commas within fields using quotes
           - Double-quote fields containing special characters
           - Maintain consistent quoting across all fields
           - No extra spaces around delimiters
           - One record per line, no line breaks within fields

        Remember: The goal is clean, professional formatting while preserving ALL original data exactly.
        Do not truncate or modify any data values, only improve their presentation.`;
      } else {
        // Enhanced Excel-specific prompt with styling and structure guidance
        prompt = `${hasMultipleSheets ? 'IMPORTANT: This Excel file contains multiple sheets (' + dataInfo.sheetCount + ' sheets). Process and improve ALL sheets in the workbook.\n\n' : ''}
        You are a data formatting specialist. Your task is to analyze this Excel data and provide a COMPLETE formatted version with significant improvements while preserving ALL original data. You can change number and name of columns. If needed you can split columns into multiple columns.

        IMPORTANT RULES:
        1. NEVER remove or modify any actual data values
        2. NEVER skip any rows or columns, but you can add new columns if needed
        3. ALWAYS return the complete dataset
        4. ALWAYS preserve numerical values exactly as they are
        5. ONLY improve formatting, headers, and structure

        Make these specific formatting improvements:
        1. Column Headers:
           - Use clear, professional, business-appropriate titles (e.g., "Revenue (USD)" instead of "Money")
           - Use proper capitalization (Title Case)
           - Add units in parentheses where applicable (e.g., "Sales Amount ($)" not just "Amount")
           - Make headers descriptive but concise
           - Use industry-standard terminology for financial, sales, or business data

        2. Data Type Formatting:
           - Format numbers appropriately with correct decimal places
           - Currency: Always include currency symbol (e.g., $, €, £) for monetary values
           - Percentages: Show % symbol and proper decimal places
           - Dates: Convert to consistent YYYY-MM-DD format
           - Text: Proper capitalization for names, titles, and proper nouns

        STYLING AND STRUCTURE REQUIREMENTS - CRITICAL:
        1. Create a professional, visually appealing output with these EXACT styling elements:
           - Header row: Dark blue background (#3B5998) with white bold text
           - Positive values: Green text (#28a745) and bold formatting
           - Negative values: Red text (#dc3545) and bold formatting
           - Apply conditional formatting to highlight high values (top 25%) with light green background
           - Apply conditional formatting to highlight low values (bottom 25%) with light red background
           - Add data visualization using color intensity for numeric comparisons
           - Use medium borders around all cells for clear table structure
           - Include a summary row with totals and averages
        
        2. Professional Structure Requirements:
           - Organize data into clear sections with appropriate headers
           - Group related information together
           - Add summary calculations where appropriate (totals, averages, etc.)
           - Ensure consistent formatting throughout the document
           - Make the data immediately readable and professional-looking

        CRITICAL OUTPUT FORMAT REQUIREMENTS:
        First, include an "improvementSummary" array at the root with 5-10 bullet strings for non-technical readers. Then return the following JSON structure:
        {
          "improvementSummary": ["Clear summary item 1", "Clear summary item 2", "..."] ,
          "title": "Professional Report Title with Business Context",
          "formattedData": [
            ["Revenue (USD)", "Sales Region", "Fiscal Quarter"],
            ["Row1Col1", "Row1Col2", "Row1Col3"],
            ["Row2Col1", "Row2Col2", "Row2Col3"]
          ],
          "columnSpecifications": [
            {"column": "Professional Header 1", "dataType": "text", "description": "Detailed professional description of this column"},
            {"column": "Professional Header 2", "dataType": "number", "description": "Detailed professional description of this column"}
          ],
          "formattingChangesMade": [
            "Detailed description of change 1",
            "Detailed description of change 2"
          ],
          "stylingRecommendations": {
            "headerStyling": {
              "backgroundColor": "#3B5998",
              "fontColor": "white",
              "isBold": true
            },
            "positiveValueStyling": {
              "fontColor": "#28a745",
              "isBold": true
            },
            "negativeValueStyling": {
              "fontColor": "#dc3545",
              "isBold": true
            },
            "sectionHeaders": {
              "backgroundColor": "#f8f9fa",
              "isBold": true
            },
            "tableBorders": "medium",
            "additionalStyles": [
              "Apply conditional formatting to highlight high values (top 25%) with light green background",
              "Apply conditional formatting to highlight low values (bottom 25%) with light red background",
              "Use data bars for visual comparison of numeric values",
              "Add summary row with totals and averages at the bottom"
            ]
          },
          "additionalNotes": "This data has been professionally formatted with industry-standard headers and styling for optimal readability and analysis."
        }
        
        IMPORTANT: The styling elements MUST be included exactly as specified in the JSON structure above. The Excel file must have colored headers, conditional formatting, and proper styling applied to be considered complete.

        Data Integrity Rules:
        - Preserve ALL original data rows and columns exactly
        - Keep all unique identifiers (IDs) unchanged
        - Maintain data relationships and ordering
        - Never truncate or omit any data

        Remember: The goal is clean, professional formatting while preserving ALL original data exactly.
        Do not truncate or modify any data values, only improve their presentation.
        
        MOST IMPORTANT: Return ONLY valid JSON in the format specified above. Do not include any markdown formatting or additional text outside the JSON structure.`;
      }
    } else if (jobType === 'excel_analysis' || jobType === 'excel_summary') {
      // Add information about multiple sheets to the prompt
      const hasMultipleSheets = !file.name.endsWith('.csv') && dataInfo.sheetCount > 1;

      prompt = `${hasMultipleSheets ? 'IMPORTANT: This Excel file contains multiple sheets (' + dataInfo.sheetCount + ' sheets). Analyze ALL sheets in the workbook and provide insights across the entire dataset.\n\n' : ''}
        You are a data analysis expert. Your task is to analyze this Excel/CSV data and provide comprehensive insights.

        IMPORTANT: Return both analysis AND the complete data with added analysis columns.

        Focus on these analytical elements:
        1. Data quality assessment (missing values, outliers, duplicates)
        2. Key statistics for numeric columns (min, max, average, distribution)
        3. Important patterns and trends in the data
        4. Relationships between columns and correlation analysis
        5. Actionable business insights based on the data

        FORMAT YOUR RESPONSE:
        1. First section: "# DATA ANALYSIS"
           - Include comprehensive analysis with clear sections
           - Use bullet points for key findings
           - Include specific metrics and statistics
        2. Second section: "# ENHANCED DATA"
           - Include the COMPLETE data in CSV format
           - Add calculated columns that enhance the analysis (e.g., totals, averages, categories)
           - Start with improved headers
        3. Third section: "# KEY INSIGHTS"
           - Summarize the 3-5 most important insights from the data

        The enhanced data must include all original data plus valuable calculated fields.
      `;
    } else if (jobType === 'excel_dashboard') {
      // Add information about multiple sheets to the prompt
      const hasMultipleSheets = !file.name.endsWith('.csv') && dataInfo.sheetCount > 1;

      prompt = `${hasMultipleSheets ? 'IMPORTANT: This Excel file contains multiple sheets (' + dataInfo.sheetCount + ' sheets). Create dashboard recommendations that utilize data from ALL sheets in the workbook.\n\n' : ''}
        You are a data visualization and dashboard expert. Your task is to analyze this Excel/CSV data and provide dashboard design recommendations.

        IMPORTANT: Return both dashboard recommendations AND the prepared data for visualization.

        Focus on these dashboard elements:
        1. Key Performance Indicators (KPIs) to highlight with specific formulas
        2. Specific chart suggestions for each key data aspect (exact chart types)
        3. Dashboard layout recommendations (sections, arrangement)
        4. Data preparation steps for visualization (aggregations, calculations)
        5. Filtering and interactive element suggestions

        FORMAT YOUR RESPONSE:
        1. First section: "# DASHBOARD RECOMMENDATIONS"
           - Include specific chart types for each data aspect
           - Provide exact KPI formulas and calculations
           - Suggest dashboard layout and organization
        2. Second section: "# PREPARED DATA"
           - Include the COMPLETE prepared data in CSV format
           - Add calculated columns needed for the dashboard
           - Structure the data for easy visualization
        3. Third section: "# IMPLEMENTATION GUIDE"
           - Step-by-step instructions for creating the recommended dashboard

        The prepared data must include all necessary calculations for the dashboard.
      `;
    } else {
      // Add information about multiple sheets to the prompt
      const hasMultipleSheets = !file.name.endsWith('.csv') && dataInfo.sheetCount > 1;

      prompt = `${hasMultipleSheets ? 'IMPORTANT: This Excel file contains multiple sheets (' + dataInfo.sheetCount + ' sheets). Process and analyze ALL sheets in the workbook.\n\n' : ''}
        You are a data analysis expert. Your task is to analyze this Excel/CSV data and provide both insights and improvements.

        IMPORTANT: Return both analysis AND the complete improved data.

        Focus on these elements:
        1. Data structure and quality assessment
        2. Key statistics and patterns in the data
        3. Formatting and presentation improvements
        4. Actionable insights from the data
        5. Recommendations for further analysis

        FORMAT YOUR RESPONSE:
        1. First section: "# DATA ANALYSIS"
           - Include comprehensive analysis with clear sections
        2. Second section: "# IMPROVED DATA"
           - Include the COMPLETE improved data in CSV format
           - Start with enhanced headers
           - Include all rows with improved formatting
        3. Third section: "# KEY INSIGHTS"
           - Summarize the most important findings

        The improved data must maintain all original values while enhancing usability.
      `;
    }

    // Add data info to the prompt
    prompt += `\n\nData overview: ${dataInfo.rows} rows × ${dataInfo.columns} columns`;

    // Add sheet count information if applicable
    if (dataInfo.sheetCount > 1) {
      prompt += `\nSpreadsheet contains ${dataInfo.sheetCount} sheets`;
    }

    if (dataInfo.headers && dataInfo.headers.length > 0) {
      prompt += `\nColumns from first sheet: ${dataInfo.headers.join(', ')}`;
    }

    // Prepend improvement summary instruction for Excel analysis output
    const excelSummaryInstruction = `\n\nAt the very TOP of your response, include a section titled "IMPROVEMENT SUMMARY" with 5-10 concise bullets describing the main improvements and key insights from the reformatted/analysed spreadsheet. After this summary, output a single line with exactly '---END_SUMMARY---' on its own, then continue with the required sections unchanged.`;
    // Inject upsell addendum (only instructions; core flow unchanged)
    const upsellAddendum = buildUpsellPromptAddendum(preUpsells);
    if (upsellAddendum) {
      prompt = `${excelSummaryInstruction}\n\n${upsellAddendum}\n\n${prompt}`;
    } else {
      prompt = `${excelSummaryInstruction}\n\n${prompt}`;
    }

    console.log("Calling Gemini API with Excel/CSV data...");

    // Create the model
    const model = genAI.getGenerativeModel({
      model: "gemini-2.5-flash",
      systemInstruction: "You are an Excel data analyst specialist skilled in formatting, analyzing, and extracting insights from spreadsheet data."
    });

    // Call the API
    const result = await model.generateContent([
      prompt,
      csvData
    ]);

    const response = result.response;
    const text = response.text();

    console.log("Gemini API response received, length:", text.length);

    // Extract the formatted data from the AI response
    let formattedData = extractFormattedDataFromAIResponse(text);

    // Ensure formattedData is an array of arrays
    if (!Array.isArray(formattedData) || formattedData.length === 0) {
      console.warn('Formatted data is not valid, creating fallback data');
      formattedData = [
        ['Data Analysis', 'Status'],
        ['Unable to parse structured data', 'Please check the original analysis']
      ];
    }

    // Ensure each row is an array
    formattedData = formattedData.map(row => {
      if (Array.isArray(row)) {
        return row;
      } else if (typeof row === 'string') {
        return [row];
      } else {
        return [String(row)];
      }
    });

    // Create a new workbook with the formatted data
    const newWorkbook = XLSX.utils.book_new();
    const newWorksheet = XLSX.utils.aoa_to_sheet(formattedData);

    // Check if we have JSON data in the response (for Excel files)
    let jsonData = null;
    let improvementSummary = null;
    try {
      // Try to extract JSON from the text (prefer the largest JSON block)
      const jsonMatches = text.match(/\{[\s\S]*\}/g);
      if (jsonMatches && jsonMatches.length > 0) {
        // Pick the longest candidate to reduce chances of partial matches
        const jsonStr = jsonMatches.sort((a, b) => b.length - a.length)[0];
        const parsed = JSON.parse(jsonStr);
        jsonData = parsed;
        improvementSummary = Array.isArray(parsed.improvementSummary) ? parsed.improvementSummary : null;
        console.log('Found JSON data in response for Excel formatting');
      }
    } catch (jsonError) {
      console.log('No valid JSON found in response, using standard formatting');
    }

    // If no summary found in JSON, try parsing a top markdown section
    const parseImprovementSummaryFromText = (fullText) => {
      try {
        const header = '# IMPROVEMENT SUMMARY';
        const idx = fullText.toUpperCase().indexOf(header);
        const formattedHeader = '# FORMATTED DATA';
        if (idx === -1) return { summary: null, cleaned: fullText };
        const nextIdx = fullText.toUpperCase().indexOf(formattedHeader, idx);
        if (nextIdx === -1) return { summary: null, cleaned: fullText };
        const block = fullText.substring(idx, nextIdx);
        const cleaned = fullText.substring(nextIdx).trimStart();
        const bullets = block
          .split('\n')
          .map(l => l.trim())
          .filter(l => /^[-*•\d+\.]/.test(l) || l.startsWith('•') || l.startsWith('-') || l.startsWith('*'))
          .map(l => l.replace(/^[-*•]\s?/, '').replace(/^\d+\.\s?/, '').trim())
          .filter(Boolean);
        return { summary: bullets.length ? bullets : null, cleaned };
      } catch (e) {
        return { summary: null, cleaned: fullText };
      }
    };

    let cleanedSuggestionsText = text;
    if (!improvementSummary) {
      const parsed = parseImprovementSummaryFromText(text);
      improvementSummary = parsed.summary;
      cleanedSuggestionsText = parsed.cleaned;
    }

    // Apply preUpsells where applicable (summary/translation for text portions)
    const runModel = async (prompt) => {
      const model = genAI.getGenerativeModel({
        model: 'gemini-2.5-flash',
        generationConfig: { maxOutputTokens: 16000, temperature: 0.2 }
      });
      const r = await model.generateContent(prompt);
      return (await r.response).text();
    };

    let translatedText = null;
    let translationLang = null;
    if (preUpsells) {
      if (preUpsells.translation) {
        try {
          translationLang = preUpsells.translation;
          const prompt = `Translate the following analysis to ${translationLang} preserving headings and bullet points. Return only translated text.\n\nCONTENT:\n${cleanedSuggestionsText}`;
          translatedText = await runModel(prompt);
        } catch (e) {
          console.warn('Translation upsell failed (excel):', e);
        }
      }
    }

    // Apply formatting to the worksheet
    const range = XLSX.utils.decode_range(newWorksheet['!ref']);

    // Apply borders to the entire table if specified
    if (jsonData && jsonData.stylingRecommendations && jsonData.stylingRecommendations.tableBorders) {
      const borderStyle = jsonData.stylingRecommendations.tableBorders;
      let borderWeight;

      switch (borderStyle) {
        case 'thin':
          borderWeight = { style: 'thin' };
          break;
        case 'medium':
          borderWeight = { style: 'medium' };
          break;
        case 'thick':
          borderWeight = { style: 'thick' };
          break;
        default:
          borderWeight = { style: 'thin' };
      }

      // Apply borders to all cells
      for (let R = range.s.r; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = newWorksheet[cellAddress] || {};

          if (!cell.s) cell.s = {};
          cell.s.border = {
            top: borderWeight,
            left: borderWeight,
            bottom: borderWeight,
            right: borderWeight
          };

          newWorksheet[cellAddress] = cell;
        }
      }
    }

    // If we have JSON data with column specifications, use that for formatting
    if (jsonData && jsonData.columnSpecifications && Array.isArray(jsonData.columnSpecifications)) {
      console.log('Applying formatting based on column specifications');

      // Check for styling recommendations
      const stylingRecs = jsonData.stylingRecommendations || {};
      const headerStyle = stylingRecs.headerStyling || { isBold: true };
      const positiveStyle = stylingRecs.positiveValueStyling || { fontColor: "#28a745" };
      const negativeStyle = stylingRecs.negativeValueStyling || { fontColor: "#dc3545" };

      // Make headers bold with styling
      for (let C = range.s.c; C <= range.e.c; C++) {
        const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        const headerCell = newWorksheet[headerAddress];
        if (headerCell) {
          headerCell.s = {
            font: {
              bold: true,
              color: { rgb: headerStyle.fontColor ? headerStyle.fontColor.replace('#', '') : '000000' }
            },
            fill: headerStyle.backgroundColor ? {
              fgColor: { rgb: headerStyle.backgroundColor.replace('#', '') },
              type: 'solid'
            } : undefined,
            alignment: { horizontal: 'center' }
          };
        }
      }

      // Apply data type-specific formatting based on column specifications
      for (let C = range.s.c; C <= range.e.c; C++) {
        const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!newWorksheet[headerAddress]) continue;

        const headerValue = newWorksheet[headerAddress].v;
        const columnSpec = jsonData.columnSpecifications.find(spec => spec.column === headerValue);

        if (columnSpec) {
          const dataType = columnSpec.dataType;

          // Apply formatting to all cells in this column
          for (let R = 1; R <= range.e.r; R++) {
            const address = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = newWorksheet[address];
            if (!cell) continue;

            // Apply formatting based on data type
            switch (dataType) {
              case 'number':
                cell.z = '#,##0.00';
                // Apply positive/negative styling based on value
                if (typeof cell.v === 'number') {
                  if (cell.v > 0) {
                    cell.s = cell.s || {};
                    cell.s.font = cell.s.font || {};
                    cell.s.font.color = { rgb: positiveStyle.fontColor ? positiveStyle.fontColor.replace('#', '') : '28a745' };
                    if (positiveStyle.isBold) cell.s.font.bold = true;
                  } else if (cell.v < 0) {
                    cell.s = cell.s || {};
                    cell.s.font = cell.s.font || {};
                    cell.s.font.color = { rgb: negativeStyle.fontColor ? negativeStyle.fontColor.replace('#', '') : 'dc3545' };
                    if (negativeStyle.isBold) cell.s.font.bold = true;
                  }
                }
                break;
              case 'date':
                cell.z = 'yyyy-mm-dd';
                break;
              case 'percentage':
                if (typeof cell.v === 'string' && cell.v.endsWith('%')) {
                  cell.v = parseFloat(cell.v) / 100;
                }
                cell.z = '0.00%';
                // Apply positive/negative styling based on value
                if (typeof cell.v === 'number') {
                  if (cell.v > 0) {
                    cell.s = cell.s || {};
                    cell.s.font = cell.s.font || {};
                    cell.s.font.color = { rgb: positiveStyle.fontColor ? positiveStyle.fontColor.replace('#', '') : '28a745' };
                    if (positiveStyle.isBold) cell.s.font.bold = true;
                  } else if (cell.v < 0) {
                    cell.s = cell.s || {};
                    cell.s.font = cell.s.font || {};
                    cell.s.font.color = { rgb: negativeStyle.fontColor ? negativeStyle.fontColor.replace('#', '') : 'dc3545' };
                    if (negativeStyle.isBold) cell.s.font.bold = true;
                  }
                }
                break;
              case 'currency':
                cell.z = '$#,##0.00';
                break;
              case 'growth':
              case 'change':
                // Special handling for growth/change metrics
                cell.z = '+0.00%;-0.00%';
                // Apply positive/negative styling based on value
                if (typeof cell.v === 'number') {
                  if (cell.v > 0) {
                    cell.s = cell.s || {};
                    cell.s.font = cell.s.font || {};
                    cell.s.font.color = { rgb: positiveStyle.fontColor ? positiveStyle.fontColor.replace('#', '') : '28a745' };
                    if (positiveStyle.isBold) cell.s.font.bold = true;
                  } else if (cell.v < 0) {
                    cell.s = cell.s || {};
                    cell.s.font = cell.s.font || {};
                    cell.s.font.color = { rgb: negativeStyle.fontColor ? negativeStyle.fontColor.replace('#', '') : 'dc3545' };
                    if (negativeStyle.isBold) cell.s.font.bold = true;
                  }
                }
                break;
            }
          }
        }
      }
    } else {
      // Apply default formatting if no column specifications
      for (let C = range.s.c; C <= range.e.c; C++) {
        const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        const headerCell = newWorksheet[headerAddress];
        if (headerCell) {
          headerCell.s = {
            font: { bold: true },
            alignment: { horizontal: 'center' }
          };
        }

        // Apply column-specific formatting
        for (let R = 1; R <= range.e.r; R++) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = newWorksheet[address];
          if (cell) {
            // Detect and format numbers
            if (typeof cell.v === 'number') {
              cell.z = '#,##0.00'; // Apply number formatting
            }
            // Detect and format dates
            else if (cell.v instanceof Date) {
              cell.z = 'yyyy-mm-dd';
            }
            // Detect and format percentages
            else if (typeof cell.v === 'string' && cell.v.endsWith('%')) {
              cell.v = parseFloat(cell.v) / 100;
              cell.z = '0.00%';
            }
          }
        }
      }
    }

    // Set column widths - use specifications if available, otherwise default
    const colWidths = [];
    if (jsonData && jsonData.columnSpecifications) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!newWorksheet[headerAddress]) continue;

        const headerValue = newWorksheet[headerAddress].v;
        const columnSpec = jsonData.columnSpecifications.find(spec => spec.column === headerValue);

        if (columnSpec) {
          const dataType = columnSpec.dataType;

          // Set width based on data type
          switch (dataType) {
            case 'number': colWidths[C] = { wch: 12 }; break;
            case 'date': colWidths[C] = { wch: 14 }; break;
            case 'currency': colWidths[C] = { wch: 14 }; break;
            case 'percentage': colWidths[C] = { wch: 10 }; break;
            default: colWidths[C] = { wch: 20 }; break;
          }
        } else {
          colWidths[C] = { wch: 20 };
        }
      }
    } else {
      // Default column widths
      for (let C = range.s.c; C <= range.e.c; C++) {
        colWidths[C] = { wch: 20 };
      }
    }
    newWorksheet['!cols'] = colWidths;

    // Add the main worksheet
    XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, 'Formatted Data');

    // If we have JSON data, add additional worksheets with metadata
    if (jsonData) {
      // Add column specifications sheet if available
      if (jsonData.columnSpecifications && jsonData.columnSpecifications.length > 0) {
        const specData = [
          ['Column', 'Data Type', 'Description'],
          ...jsonData.columnSpecifications.map(spec => [
            spec.column,
            spec.dataType || 'text',
            spec.description || ''
          ])
        ];

        const specSheet = XLSX.utils.aoa_to_sheet(specData);
        specSheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 50 }];

        // Apply styling to header row
        const headerStyle = jsonData.stylingRecommendations?.headerStyling || { isBold: true };
        for (let C = 0; C < 3; C++) {
          const headerAddress = XLSX.utils.encode_cell({ r: 0, c: C });
          const headerCell = specSheet[headerAddress];
          if (headerCell) {
            headerCell.s = {
              font: {
                bold: true,
                color: { rgb: headerStyle.fontColor ? headerStyle.fontColor.replace('#', '') : '000000' }
              },
              fill: headerStyle.backgroundColor ? {
                fgColor: { rgb: headerStyle.backgroundColor.replace('#', '') },
                type: 'solid'
              } : undefined,
              alignment: { horizontal: 'center' }
            };
          }
        }

        XLSX.utils.book_append_sheet(newWorkbook, specSheet, "Column Specifications");
      }

      // Add formatting changes sheet if available
      if (jsonData.formattingChangesMade && jsonData.formattingChangesMade.length > 0) {
        const changesData = [
          ['Formatting Changes Made'],
          ...jsonData.formattingChangesMade.map(change => [change])
        ];

        const changesSheet = XLSX.utils.aoa_to_sheet(changesData);
        changesSheet['!cols'] = [{ wch: 100 }];

        // Apply styling to header row
        const headerStyle = jsonData.stylingRecommendations?.headerStyling || { isBold: true };
        const headerAddress = XLSX.utils.encode_cell({ r: 0, c: 0 });
        const headerCell = changesSheet[headerAddress];
        if (headerCell) {
          headerCell.s = {
            font: {
              bold: true,
              color: { rgb: headerStyle.fontColor ? headerStyle.fontColor.replace('#', '') : '000000' }
            },
            fill: headerStyle.backgroundColor ? {
              fgColor: { rgb: headerStyle.backgroundColor.replace('#', '') },
              type: 'solid'
            } : undefined,
            alignment: { horizontal: 'center' }
          };
        }

        XLSX.utils.book_append_sheet(newWorkbook, changesSheet, "Formatting Changes");
      }

      // Add styling recommendations sheet if available
      if (jsonData.stylingRecommendations && jsonData.stylingRecommendations.additionalStyles) {
        const stylingData = [
          ['Styling Recommendations'],
          ['Header Styling', JSON.stringify(jsonData.stylingRecommendations.headerStyling || {})],
          ['Positive Value Styling', JSON.stringify(jsonData.stylingRecommendations.positiveValueStyling || {})],
          ['Negative Value Styling', JSON.stringify(jsonData.stylingRecommendations.negativeValueStyling || {})],
          ['Section Headers', JSON.stringify(jsonData.stylingRecommendations.sectionHeaders || {})],
          ['Table Borders', jsonData.stylingRecommendations.tableBorders || 'medium'],
          ['']
        ];

        // Add additional styling recommendations
        if (Array.isArray(jsonData.stylingRecommendations.additionalStyles)) {
          stylingData.push(['Additional Styling Recommendations']);
          jsonData.stylingRecommendations.additionalStyles.forEach(style => {
            stylingData.push([style]);
          });
        }

        const stylingSheet = XLSX.utils.aoa_to_sheet(stylingData);
        stylingSheet['!cols'] = [{ wch: 30 }, { wch: 70 }];

        // Apply styling to header rows
        const headerStyle = jsonData.stylingRecommendations.headerStyling || { isBold: true };
        const headerAddresses = [
          XLSX.utils.encode_cell({ r: 0, c: 0 }),
          XLSX.utils.encode_cell({ r: 7, c: 0 })
        ];

        headerAddresses.forEach(address => {
          const cell = stylingSheet[address];
          if (cell) {
            cell.s = {
              font: {
                bold: true,
                color: { rgb: headerStyle.fontColor ? headerStyle.fontColor.replace('#', '') : '000000' }
              },
              fill: headerStyle.backgroundColor ? {
                fgColor: { rgb: headerStyle.backgroundColor.replace('#', '') },
                type: 'solid'
              } : undefined,
              alignment: { horizontal: 'center' }
            };
          }
        });

        XLSX.utils.book_append_sheet(newWorkbook, stylingSheet, "Styling Guide");
      }
    }

    // Convert the workbook to a blob
    const wbout = XLSX.write(newWorkbook, { bookType: fileType === 'csv' ? 'csv' : 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: fileType === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    return {
      enhancement_suggestions: cleanedSuggestionsText,
      improvementSummary: improvementSummary || null,
      original_data: JSON.stringify(originalDataArray),
      file_type: fileType,
      enhanced_file: blob, // Add the formatted file as a blob
      translated_text: translatedText || null,
      translation_lang: translationLang || null
    };

  } catch (error) {
    console.error("Error in processExcelWithGemini:", error);

    // Generate a fallback analysis if there's an error
    const fallbackAnalysis = generateFallbackAnalysis(dataInfo, jobType);

    return {
      enhancement_suggestions: fallbackAnalysis,
      original_data: JSON.stringify(originalDataArray || []),
      file_type: fileType,
      error: error.message
    };
  }
};

// Helper function to extract formatted data from AI response
function extractFormattedDataFromAIResponse(aiResponse) {
  try {
    // First check if the response is JSON (for Excel files)
    try {
      // Try to find JSON in the response
      const jsonMatch = aiResponse.match(/(\{[\s\S]*\})/);
      if (jsonMatch) {
        const jsonStr = jsonMatch[1];
        const jsonData = JSON.parse(jsonStr);

        // If we have formattedData in JSON format, use it
        if (jsonData && jsonData.formattedData && Array.isArray(jsonData.formattedData)) {
          console.log('Successfully parsed JSON formatted data');
          return jsonData.formattedData;
        }
      }
    } catch (jsonError) {
      console.log('Response is not in JSON format, trying CSV format');
      // If JSON parsing fails, continue with CSV parsing
    }

    // Find the formatted data section (for CSV files)
    const formattedDataSection = aiResponse.split('# FORMATTED DATA')[1]?.split('#')[0];
    if (!formattedDataSection) {
      throw new Error('Could not find formatted data section in AI response');
    }

    // Parse the CSV data
    const rows = formattedDataSection.trim().split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        // Handle quoted fields with commas
        const row = [];
        let inQuote = false;
        let currentValue = "";

        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"' && (i === 0 || line[i - 1] !== '\\')) {
            inQuote = !inQuote;
          } else if (char === ',' && !inQuote) {
            row.push(currentValue.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
            currentValue = "";
          } else {
            currentValue += char;
          }
        }

        if (currentValue) {
          row.push(currentValue.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
        }

        return row;
      });

    return rows;
  } catch (error) {
    console.error('Error extracting formatted data from AI response:', error);
    throw new Error('Failed to parse formatted data from AI response');
  }
}

// Generate a basic fallback analysis if all AI models fail
const generateFallbackAnalysis = (dataInfo, jobType) => {
  let analysis = "Unable to generate AI analysis due to service limitations. Here's a basic analysis of your data:\n\n";

  // Add basic data statistics
  analysis += `Your data has ${dataInfo.rows} rows and ${dataInfo.columns} columns.\n`;

  if (dataInfo.headers && dataInfo.headers.length > 0) {
    analysis += `\nColumns identified: ${dataInfo.headers.join(', ')}\n`;
  }

  // Add job-specific recommendations
  if (jobType === 'excel_formatting') {
    analysis += `\nFormatting Recommendations:\n`;
    analysis += `1. Ensure your column headers are clear and descriptive\n`;
    analysis += `2. Use appropriate data types for each column (text, number, date, etc.)\n`;
    analysis += `3. Format dates consistently (e.g., YYYY-MM-DD)\n`;
    analysis += `4. Use number formatting for numeric columns (currency, percentage, etc.)\n`;
    analysis += `5. Consider using color coding or conditional formatting for key insights\n`;
  } else if (jobType === 'excel_analysis' || jobType === 'excel_summary') {
    analysis += `\nAnalysis Recommendations:\n`;
    analysis += `1. Check for missing or incomplete data in all columns\n`;
    analysis += `2. Look for outliers in numeric columns that may skew your analysis\n`;
    analysis += `3. Consider calculating summary statistics (min, max, average) for numeric columns\n`;
    analysis += `4. Examine relationships between different columns\n`;
    analysis += `5. Create pivot tables to summarize your data effectively\n`;
  } else if (jobType === 'excel_dashboard') {
    analysis += `\nDashboard Recommendations:\n`;
    analysis += `1. Identify 3-5 key metrics to highlight in your dashboard\n`;
    analysis += `2. Use appropriate chart types: bar charts for comparisons, line charts for trends\n`;
    analysis += `3. Group related metrics together for easier understanding\n`;
    analysis += `4. Include filters to allow users to explore different segments of data\n`;
    analysis += `5. Keep your dashboard clean and focused on key insights\n`;
  }

  analysis += `\nPlease try again later or contact support if you need more detailed analysis.`;

  return analysis;
};

export const processDocumentWithGemini = async (file, jobType, enhancementType = null, preUpsells = null) => {
  try {
    console.log(`Processing document file with Gemini: ${file.name}, job type: ${jobType}, enhancement type: ${enhancementType || 'none'}`);

    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 10) {
      console.warn(`File size (${fileSizeInMB.toFixed(2)}MB) exceeds recommended limit for Gemini API`);
    }

    // Determine file type and MIME type
    let mimeType = "text/plain";
    let extractedText = "";
    let fileContent = null;
    const fileType = file.name.split('.').pop().toLowerCase();
    const fileName = file.name.toLowerCase();

    // Check if this is a development copy summary document based on filename
    const isDevelopmentCopy = fileName.includes('development copy') ||
      fileName.includes('dev copy') ||
      fileName.includes('copy summary') ||
      fileName.includes('dev summary');

    // If it's a development copy, set a special job type
    const effectiveJobType = isDevelopmentCopy ? 'document_development' : jobType;

    if (fileType === 'pdf') {
      mimeType = "text/plain";
      try {
        // For PDFs, extract text using PDF.js
        console.log("PDF file detected - extracting text content with PDF.js");
        extractedText = await extractPdfText(file);

        // Clean the extracted text to remove problematic characters
        extractedText = cleanTextContent(extractedText);

        console.log(`Extracted and cleaned ${extractedText.length} characters from PDF`);

        // If extraction is too small, we'll fall back to multimodal API
        if (!extractedText || extractedText.length < 100 || extractedText.includes('[Error extracting PDF text:')) {
          console.warn("Text extraction from PDF yielded limited results, falling back to multimodal API");
          mimeType = "application/pdf";
          fileContent = await readFileAsArrayBuffer(file);
        }
      } catch (pdfError) {
        console.error("Error extracting text from PDF:", pdfError);
        // Fall back to multimodal API
        mimeType = "application/pdf";
        fileContent = await readFileAsArrayBuffer(file);
      }
    } else if (fileType === 'docx' || fileType === 'doc') {
      mimeType = "text/plain";

      try {
        console.log("DOCX file detected - extracting text content with mammoth");

        // Use mammoth to extract text from DOCX file
        extractedText = await extractDocxText(file);

        // Clean the extracted text to remove problematic characters
        extractedText = cleanTextContent(extractedText);

        console.log(`Extracted and cleaned ${extractedText.length} characters of text from DOCX`);

        // If extraction is too small, add a note
        if (extractedText.length < 200) {
          console.warn("Limited text extraction from DOCX file");
          extractedText = "NOTE: The text extraction from this DOCX file was limited. For better results, please consider converting to plain text or PDF format before uploading.\n\n" + extractedText;
        }
      } catch (docxError) {
        console.error("Error handling DOCX file:", docxError);
        extractedText = "Unable to extract text from this DOCX file. For better results, please convert to plain text or PDF format before uploading.";
      }
    } else if (fileType === 'txt') {
      mimeType = "text/plain";
      // For TXT files, we can extract the text directly
      extractedText = await readFileAsText(file);

      // Clean the extracted text to remove problematic characters
      extractedText = cleanTextContent(extractedText);

      console.log("Text file content (first 200 chars):", extractedText.substring(0, 200) + "...");
    } else {
      throw new Error(`Unsupported file type: ${fileType}. Please upload a PDF, DOCX, or TXT file.`);
    }

    // Generate prompt based on job type and enhancement type
    let prompt = getDocumentPrompt(effectiveJobType, enhancementType, file.name, fileType === 'pdf');

    // Reinstate improvement summary instruction for documents
    const summaryInstruction = `\n\nCRITICAL: At the very TOP of your response, include a section titled "IMPROVEMENT SUMMARY" with 5-10 concise bullet points describing what you improved (tone, grammar, structure, formatting, clarity, headings, etc.) for a non-technical reader. After this summary, output a single line with exactly '---END_SUMMARY---' on its own. Then provide the full improved document exactly as requested in the prompt. The summary must NOT be part of the improved document body. DO NOT add any executive summary or business summary to the document content.`;
    // Inject upsell addendum (only changes prompt instructions)
    const upsellAddendum = buildUpsellPromptAddendum(preUpsells);
    if (upsellAddendum) {
      prompt = `${summaryInstruction}\n\n${upsellAddendum}\n\n${prompt}`;
    } else {
      prompt = `${summaryInstruction}\n\n${prompt}`;
    }

    // Add file info to the prompt
    prompt += `\n\nFile info: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`;

    // Add special note for development copy analysis
    if (isDevelopmentCopy) {
      prompt += `\n\nNOTE: This appears to be a development copy or copy summary document. Please analyze it with special attention to the marketing copy, messaging, tone, and brand voice.`;
    }

    console.log("Calling Gemini API with document data...");

    // Try multiple models in sequence until one succeeds
    let aiResponse = '';
    let modelUsed = '';

    // Use gemini-2.5-flash for all processing
    const models = [
      "gemini-2.5-flash"  // Primary model
    ];

    // Add emoji restriction instruction for PDF files
    const isPdfOptimized = fileType === 'pdf';
    const emojiRestriction = isPdfOptimized ?
      "\n\nCRITICAL EMOJI RESTRICTIONS FOR PDF OUTPUT:\n- DO NOT use any emojis in your response\n- If the input file contains emojis, remove them completely\n- Use only text-based formatting and indicators\n- This document will be converted to PDF format which does not support emojis\n" : "";

    let lastError = null;

    for (const modelName of models) {
      try {
        console.log(`Trying model: ${modelName}`);

        // Configure model with high max output tokens to ensure complete outputs
        const model = genAI.getGenerativeModel({
          model: modelName,
          generationConfig: {
            maxOutputTokens: 100000,  // Set to maximum allowed value
            temperature: 0.2,         // Lower temperature for more consistent outputs
          }
        });

        // If we have extracted text, always use text-only API
        if (extractedText || fileType === 'txt' || fileType === 'docx' || fileType === 'doc') {
          // For text files or documents where we extracted text, use text-only API
          const fullPrompt = `${prompt}${emojiRestriction}\n\nDocument content:\n${extractedText}`;
          console.log(`Sending prompt to ${modelName} with extracted text (length: ${fullPrompt.length} chars)`);
          console.log(`Text sample: ${extractedText.substring(0, 100)}...`);

          const result = await model.generateContent(fullPrompt);
          const response = await result.response;
          aiResponse = response.text();
        } else if (fileContent) {
          // For binary files (PDF only), use multimodal API
          try {
            // Convert file to base64
            const base64Data = await arrayBufferToBase64(fileContent);

            // Call with both text and file content
            const genContent = {
              parts: [
                { text: prompt + emojiRestriction },
                {
                  inlineData: {
                    mimeType: mimeType,
                    data: base64Data
                  }
                }
              ]
            };

            // For multimodal content, use gemini-2.5-flash
            if (modelName === 'gemini-2.5-flash') {
              console.log(`Sending multimodal content to ${modelName}`);
              const result = await model.generateContent(genContent);
              const response = await result.response;
              aiResponse = response.text();
            } else {
              // For older models, try text-only with a more generic prompt
              console.log(`Model ${modelName} doesn't support multimodal, using text-only prompt`);
              const textOnlyPrompt = `${prompt}${emojiRestriction}\n\nI can't directly view the contents of your ${fileType.toUpperCase()} file, but I'll provide general guidance on document enhancement based on your request. If you need specific feedback, please share key sections or excerpts of the document's content.`;
              const result = await model.generateContent(textOnlyPrompt);
              const response = await result.response;
              aiResponse = response.text();
            }
          } catch (multimodalError) {
            console.error(`Error with multimodal content for ${modelName}:`, multimodalError.message);

            // Try text-only for the same model as fallback
            if (modelName === 'gemini-2.5-flash') {
              console.log(`Trying ${modelName} with text-only content...`);
              const textOnlyPrompt = `${prompt}${emojiRestriction}\n\nI can't directly view the contents of your ${fileType.toUpperCase()} file, but I'll provide general guidance on document enhancement based on your request. If you need specific feedback, please share key sections or excerpts of the document's content.`;
              const result = await model.generateContent(textOnlyPrompt);
              const response = await result.response;
              aiResponse = response.text();
            } else {
              // Rethrow for other models so we can try the next model
              throw multimodalError;
            }
          }
        }

        // If we get here, the model succeeded
        modelUsed = modelName;
        console.log(`Successfully used model: ${modelName}`);
        break;

      } catch (error) {
        console.error(`Error with model ${modelName}:`, error.message);
        lastError = error;

        // Check if it's a quota error, in which case we should try another model
        if (error.message && (
          error.message.includes("quota") ||
          error.message.includes("rate limit") ||
          error.message.includes("resource exhausted")
        )) {
          console.log(`Quota exceeded for model ${modelName}, trying next model...`);
          continue;
        }
      }
    }

    // If all models failed, provide a fallback response
    if (!aiResponse) {
      console.error("All models failed, providing fallback response");

      // Generate a basic analysis based on the document type and job
      aiResponse = generateFallbackDocumentAnalysis(fileType, effectiveJobType, enhancementType, file.name);
      modelUsed = "fallback";
    }

    // Parse improvement summary and strip it from the enhanced content
    const parseImprovementSummary = (text) => {
      try {
        const headerIdx = text.toUpperCase().indexOf('IMPROVEMENT SUMMARY');
        const marker = '---END_SUMMARY---';
        if (headerIdx === -1) return { summary: null, cleaned: text };
        const markerIdx = text.indexOf(marker, headerIdx);
        if (markerIdx === -1) return { summary: null, cleaned: text };
        const summaryBlock = text.substring(headerIdx, markerIdx);
        const cleaned = text.substring(markerIdx + marker.length).trim();
        // Extract bullet lines
        const bullets = summaryBlock
          .split('\n')
          .map(l => l.trim())
          .filter(l => /^[-*•\d+\.]/.test(l) || l.startsWith('•') || l.startsWith('-') || l.startsWith('*'))
          .map(l => l.replace(/^[-*•]\s?/, '').replace(/^\d+\.\s?/, '').trim())
          .filter(Boolean);
        return { summary: bullets.length ? bullets : null, cleaned };
      } catch (e) {
        return { summary: null, cleaned: text };
      }
    };

    const { summary: improvementSummary, cleaned: cleanedContent } = parseImprovementSummary(aiResponse);

    // Apply preUpsells where applicable (summary/translation for text portions)
    const runModel = async (prompt) => {
      const model = genAI.getGenerativeModel({
        model: 'gemini-2.5-flash',
        generationConfig: { maxOutputTokens: 16000, temperature: 0.2 }
      });
      const r = await model.generateContent(prompt);
      return (await r.response).text();
    };

    let translatedText = null;
    let translationLang = null;
    if (preUpsells) {
      if (preUpsells.translation) {
        try {
          translationLang = preUpsells.translation;
          const prompt = `Translate the following analysis to ${translationLang} preserving headings and bullet points. Return only translated text.\n\nCONTENT:\n${cleanedContent}`;
          translatedText = await runModel(prompt);
        } catch (e) {
          console.warn('Translation upsell failed (excel):', e);
        }
      }
    }

    // Return the result with the model used
    return {
      enhancement_suggestions: cleanedContent || aiResponse,
      improvementSummary: improvementSummary || null,
      file_type: fileType,
      job_type: jobType,
      effective_job_type: effectiveJobType,
      enhancement_type: enhancementType,
      model_used: modelUsed,
      extraction_status: extractedText ? "success" : "not_applicable",
      extraction_method: fileType === 'docx' || fileType === 'doc'
        ? "mammoth"
        : (fileType === 'pdf' && extractedText && extractedText.length > 100)
          ? "pdfjs"
          : (fileType === 'pdf' && fileContent)
            ? "multimodal"
            : "standard",
      text_length: extractedText ? extractedText.length : 0,
      is_development_copy: isDevelopmentCopy,
      translated_text: translatedText || null,
      translation_lang: translationLang || null
    };

  } catch (error) {
    console.error(`Error processing document file with Gemini: ${error.message}`);
    throw new Error(`Failed to process document file: ${error.message}`);
  }
};

// Helper function to extract readable text from DOCX content using mammoth
const extractDocxText = async (file) => {
  console.log("Extracting text from DOCX file using mammoth:", file.name);

  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await readFileAsArrayBuffer(file);
    console.log("File read as ArrayBuffer, size:", arrayBuffer.byteLength, "bytes");

    // Use mammoth to extract text
    console.log("Calling mammoth.extractRawText...");
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = result.value || "";

    console.log(`Successfully extracted ${text.length} characters from DOCX using mammoth`);
    console.log("Text excerpt (first 200 chars):", text.substring(0, 200));

    // If we got warnings from mammoth, log them
    if (result.messages && result.messages.length) {
      console.log("Mammoth extraction warnings:", result.messages);
    }

    return text;
  } catch (error) {
    console.error("Error extracting text from DOCX with mammoth:", error);
    console.error("Error details:", error.message);

    // Fall back to the original method if mammoth fails
    console.log("Falling back to alternate extraction method...");
    return extractDocxTextFallback(await readFileAsText(file));
  }
};

// Rename the old extraction function as a fallback
const extractDocxTextFallback = (rawText) => {
  // This function tries to extract readable text from the raw DOCX content 
  // by looking for chunks of text that look human-readable

  console.log("Falling back to text extraction from raw DOCX content...");

  // First, try to extract content between <w:t> tags (Word XML)
  let extractedText = '';
  const contentRegex = /<w:t[^>]*>([^<]+)<\/w:t>/g;
  let match;

  while ((match = contentRegex.exec(rawText)) !== null) {
    extractedText += match[1] + ' ';
  }

  // If we found content from XML tags
  if (extractedText.trim().length > 100) {
    console.log(`Found ${extractedText.length} characters of text from XML tags`);
    return extractedText.trim();
  }

  // If XML extraction didn't work, look for proper paragraphs that might be content
  // This regex looks for chunks of text that are likely paragraphs (20+ characters)
  console.log("XML extraction didn't produce good results, trying paragraph extraction...");
  const paragraphRegex = /[\w\s,.;:'"!?-]{20,}/g;
  const textChunks = [];

  while ((match = paragraphRegex.exec(rawText)) !== null) {
    // Skip obvious XML/markup content 
    if (!match[0].includes('xmlns') &&
      !match[0].includes('http:') &&
      !match[0].includes('.xml') &&
      !match[0].includes('<?xml') &&
      !match[0].includes('<w:') &&
      !/^[ \t\r\n]+$/.test(match[0])) {
      textChunks.push(match[0].trim());
    }
  }

  console.log(`Found ${textChunks.length} potential text chunks`);

  // If we found some chunks, join them together
  if (textChunks.length > 0) {
    extractedText = textChunks.join('\n\n');

    // Clean up the text
    extractedText = extractedText
      .replace(/\s+/g, ' ')    // Normalize whitespace
      .replace(/\s([.,;:!?])/g, '$1')  // Fix spacing before punctuation
      .trim();
  }

  return extractedText;
};

// Update the getDocumentPrompt function to handle PDF output
const getDocumentPrompt = (jobType, enhancementType = null, fileName = '', isPdfOptimized = false) => {
  let prompt = "";

  // PDF-optimized versions without emojis
  if (isPdfOptimized) {
    // Special case for development copy documents - PDF version
    if (jobType === 'document_development') {
      return `You are an expert marketing and UX copywriting consultant. Your task is to improve this development copy document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

CRITICAL: This document will be converted to PDF format. DO NOT use emojis under any circumstances. Use only text-based indicators and formatting.

- Use descriptive text prefixes for lists: "Strategy 1:", "Key Point 1:", "Method 1:" etc.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Enhance clarity, consistency, and brand voice
3. Improve marketing messaging and persuasiveness
4. Optimize emotional appeal and connection with target audience
5. Strengthen calls-to-action
6. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a compelling, professional main title that captures the essence of the document
2. Add a subtitle or description that provides context (if appropriate)
3. Use descriptive section headings that guide the reader
4. Ensure the title is marketing-focused and engaging

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for a professional document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key points, important terms, benefits, and calls-to-action
4. Use bullet lists (- item) and numbered lists (1. item) with text-based indicators for visual enhancement
5. Use > for blockquotes or testimonials
6. Create clear section breaks between major content areas
7. Format tables using Markdown table syntax if needed
8. Add text-based indicators to section headings like: "Strategy 1:", "Growth Tactic 2:", "Implementation 3:"

FORMAT YOUR RESPONSE:
1. Start with a compelling main title as level 1 heading (# Professional Title Here)
2. Add a subtitle or brief description if appropriate (as regular text)
3. Then include the FULL improved document text with proper formatting - INCLUDE EVERY PAGE AND SECTION
4. At the end, add a section titled "## Document Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT use emojis
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# Strategic Marketing Communication Plan
*A comprehensive guide to effective customer engagement and brand positioning*

## Getting Started
### Key Strategies
Strategy 1: **Target Audience Analysis** - Identify your core customers
Growth 2: **Metrics Setup** - Track meaningful KPIs
Quick Win 3: **Implementation** - Deploy immediate improvements

[Then the full document content follows...]`;
    }

    // PDF-optimized prompts for other document types
    switch (jobType) {
      case "document_general":
        prompt = `You are a professional editor and proofreader. Your task is to improve this document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

CRITICAL: This document will be converted to PDF format. DO NOT use emojis under any circumstances. Use only text-based indicators and formatting.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Improve clarity and readability 
3. Enhance sentence structure and flow
4. Fix any inconsistencies or awkward phrasing
5. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a clear, professional main title that accurately represents the document content
2. Add a subtitle or description that provides additional context (if needed)
3. Use descriptive section headings that improve navigation
4. Ensure the title is professional and informative

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for a professional document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key points, important information, and critical details
4. Use bullet lists (- item) and numbered lists (1. item) with text-based prefixes for better organization
5. Use > for blockquotes or important notes
6. Create clear section breaks between major content areas
7. Format tables using Markdown table syntax if needed
8. Add text-based indicators to enhance readability and structure

FORMAT YOUR RESPONSE:
1. Start with a professional main title as level 1 heading (# Professional Title Here)
2. Add a subtitle or brief description if appropriate (as regular text or italic)
3. Then include the FULL improved document text with proper formatting - INCLUDE EVERY PAGE AND SECTION
4. At the end, add a section titled "## Document Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT use emojis
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# Professional Document Title
*Clear description of document purpose and scope*

## Main Content
### Key Points
Point 1: **Important Detail** - Explanation here
Critical 2: **Essential Information** - Details follow

[Then the full document content follows...]`;
        break;

      case "document_business":
        prompt = `You are a professional business communication consultant. Your task is to improve this business document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

CRITICAL: This document will be converted to PDF format. DO NOT use emojis under any circumstances. Use only text-based indicators and formatting.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Enhance clarity, conciseness, and professional tone
3. Improve business messaging and value proposition
4. Optimize structure and organization for business context
5. Make the document more persuasive and action-oriented
6. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a compelling business title that reflects the document's strategic value
2. Add a professional subtitle that clarifies the business context
3. Use business-appropriate section headings (Overview, Key Points, Strategic Analysis, etc.)
4. Ensure the title conveys authority and professionalism

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for a professional business document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key business points, important metrics, ROI figures, and strategic insights
4. Use bullet lists (- item) and numbered lists (1. item) with text-based prefixes for processes and key points
5. Format tables using Markdown table syntax for data presentation
6. Create clear section breaks between major content areas
7. Use > for highlighting important quotes, testimonials, or key insights
8. Add text-based business indicators to enhance structure and readability

FORMAT YOUR RESPONSE:
1. Start with a strategic business title as level 1 heading (# Strategic Business Title)
2. Add a professional subtitle that provides business context (as italic text)
3. Then include the FULL improved document text with proper formatting - INCLUDE EVERY PAGE AND SECTION
4. At the end, add a section titled "## Business Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED BUSINESS DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT use emojis
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# Strategic Business Operations Plan
*A comprehensive framework for organizational excellence and growth optimization*

## Strategic Overview
### Key Objectives
Revenue 1: **Growth Target** - 25% increase in Q4
Market 2: **Expansion Plan** - Enter 3 new geographic markets
Operations 3: **Excellence Initiative** - Reduce costs by 15%

[Then the full document content follows...]`;
        break;

      case "document_blog":
        prompt = `You are an expert content marketing consultant. Your task is to improve this blog post while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

CRITICAL: This document will be converted to PDF format. DO NOT use emojis under any circumstances. Use only text-based indicators and formatting. 

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Enhance the headline/title for better engagement
3. Improve reader engagement and content flow
4. Optimize structure (intro, body, conclusion)
5. Enhance readability through better formatting suggestions
6. Strengthen the call-to-action
7. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create an engaging, SEO-friendly blog title that captures attention
2. Add a compelling subtitle or meta description that hooks the reader
3. Use engaging section headings that encourage continued reading
4. Ensure the title is optimized for both search and social sharing

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for an engaging blog post
2. Use # for the main title (will be centered), ## for section headings, and ### for subsections
3. Use **bold** extensively for emphasis on key points, important terms, benefits, and actionable tips
4. Use *italics* for quotes or to add subtle emphasis
5. Use bullet lists (- item) and numbered lists (1. item) with text-based prefixes to break up text
6. Use > for highlighting important quotes, statistics, or key takeaways
7. Add appropriate section breaks between introduction, main content, and conclusion
8. Format any data or statistics in an easily digestible way
9. Add text-based indicators to enhance readability

FORMAT YOUR RESPONSE:
1. Start with an engaging blog title as level 1 heading (# Compelling Blog Title)
2. Add a hook or meta description that draws readers in (as italic text)
3. Then include the FULL improved blog post with proper formatting - INCLUDE EVERY SECTION
4. At the end, add a section titled "## Content Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED BLOG POST" or "Below is the complete improved version of your blog post:".
DO NOT use emojis 
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# 10 Proven Strategies to Transform Your Business in 2024
*Discover the actionable insights that industry leaders use to drive unprecedented growth*

## Quick Start Guide
### Power Tips
Method 1: **The "Brain Dump" Technique (2 minutes)** - Clear your mind instantly
Strategy 2: **Priority Matrix Method** - Focus on what matters most
Power Hour 3: **Daily Productivity Block** - Transform your output

[Then the full blog content follows...]`;
        break;

      case "document_academic":
        prompt = `You are an academic writing consultant. Your task is to improve this academic document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

CRITICAL: This document will be converted to PDF format. DO NOT use emojis under any circumstances. Use only text-based indicators and formatting. 

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Enhance academic tone and formality
3. Improve argument structure and logical flow
4. Clarify any unclear concepts or terminology
5. Strengthen the academic rigor of the document
6. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a precise, academic title that clearly states the research focus
2. Add a subtitle that specifies the scope, methodology, or approach
3. Use formal academic section headings (Abstract, Introduction, Literature Review, etc.)
4. Ensure the title follows academic conventions and standards
5. Use minimal, text-based indicators where appropriate for academic context

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for an academic document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key terms, concepts, findings, and important research points
4. Use *italics* for book titles, journal names, foreign terms, or scientific names
5. Use numbered lists (1. item) for sequential steps, procedures, or arguments
6. Use bullet lists (- item) for non-sequential items, examples, or key points
7. Format citations and references consistently
8. Use > for block quotes from academic sources or important theoretical statements
9. Format tables using Markdown table syntax for data presentation and results
10. Use minimal text-based indicators only where they enhance clarity

FORMAT YOUR RESPONSE:
1. Start with a formal academic title as level 1 heading (# Academic Research Title)
2. Add a descriptive subtitle that clarifies scope and methodology (as italic text)
3. Then include the FULL improved academic document with proper formatting - INCLUDE EVERY SECTION
4. At the end, add a section titled "## Academic Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED ACADEMIC DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT use emojis 
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# The Impact of Digital Transformation on Organizational Performance
*A Quantitative Analysis of Technology Adoption in Fortune 500 Companies*

## Methodology
### Research Design
Survey 1: **Quantitative Analysis** - 500 companies analyzed
Analysis 2: **Statistical Methods** - Regression modeling applied
Variables 3: **Key Metrics** - Technology adoption indicators

[Then the full academic content follows...]`;
        break;

      default:
        // Generic PDF-optimized document enhancement prompt
        prompt = `You are a professional editor and document enhancement specialist. Your task is to improve this document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

CRITICAL: This document will be converted to PDF format. DO NOT use emojis under any circumstances. Use only text-based indicators and formatting. 

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Improve clarity, organization, and effectiveness
3. Enhance readability and flow
4. Optimize structure and formatting
5. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a clear, professional main title that represents the document purpose
2. Add a subtitle or description that provides context
3. Use descriptive section headings that improve document navigation
4. Ensure the title is appropriate for the document type and audience

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for a professional document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key points, important information, and critical details
4. Use bullet lists (- item) and numbered lists (1. item) with text-based prefixes appropriately
5. Use > for blockquotes, important notes, or testimonials
6. Create clear section breaks between major content areas
7. Format tables using Markdown table syntax if needed
8. Add text-based indicators to enhance structure and readability

FORMAT YOUR RESPONSE:
1. Start with a professional main title as level 1 heading (# Professional Document Title)
2. Add a subtitle or brief description if appropriate (as italic text)
3. Then include the FULL improved document text with proper formatting - INCLUDE EVERY PAGE AND SECTION
4. At the end, add a section titled "## Document Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT use emojis 
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# Professional Document Title
*Comprehensive guide and reference for [specific purpose]*

## Main Content
### Key Sections
Point 1: **Essential Information** - Core details here
Strategy 2: **Important Insights** - Critical information follows

[Then the full document content follows...]`;
    }

    return prompt;
  }

  // Otherwise, use the standard document prompt logic
  switch (jobType) {
    case "document_general":
      prompt = `You are a professional editor and proofreader. Your task is to improve this document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Improve clarity and readability 
3. Enhance sentence structure and flow
4. Fix any inconsistencies or awkward phrasing
5. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a clear, professional main title that accurately represents the document content
2. Add a subtitle or description that provides additional context (if needed)
3. Use descriptive section headings that improve navigation
4. Ensure the title is professional and informative
5. Add professional emojis to enhance visual appeal and readability

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for a professional document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key points, important information, and critical details
4. Use bullet lists (- item) and numbered lists (1. item) with professional emojis for better organization
5. Use > for blockquotes or important notes
6. Create clear section breaks between major content areas
7. Format tables using Markdown table syntax if needed
8. Add professional emojis to enhance readability and structure

EMOJI USAGE GUIDELINES:
- Use professional, appropriate emojis: 📝 📊 🔍 💡 🎯 📈 ⚡ 🔑 ✨ 📋 🌟 🏆 💼 📱 🎨 🚀 ⭐ 💪 🔥 📌
- Add emojis to numbered lists: "📝 1. First Point", "💡 2. Key Insight"
- Use emojis in section headings: "## 📊 Overview", "### 🔍 Details"
- Keep emoji usage professional and relevant to content

FORMAT YOUR RESPONSE:
1. Start with a professional main title as level 1 heading (# 📋 Professional Title Here)
2. Add a subtitle or brief description if appropriate (as regular text or italic)
3. Then include the FULL improved document text with proper formatting - INCLUDE EVERY PAGE AND SECTION
4. At the end, add a section titled "## ✨ Document Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# 📋 Professional Document Title
*Clear description of document purpose and scope*

## 🎯 Main Content
### 💡 Key Points
📝 1. **Important Detail** - Explanation here
🔍 2. **Critical Information** - Details follow

[Then the full document content follows...]`;
      break;

    case "document_business":
      prompt = `You are a professional business communication consultant. Your task is to improve this business document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Enhance clarity, conciseness, and professional tone
3. Improve business messaging and value proposition
4. Optimize structure and organization for business context
5. Make the document more persuasive and action-oriented
6. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a compelling business title that reflects the document's strategic value
2. Add a professional subtitle that clarifies the business context
3. Use business-appropriate section headings (Overview, Key Points, Strategic Analysis, etc.)
4. Ensure the title conveys authority and professionalism
5. Add professional business emojis to enhance visual appeal and structure

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for a professional business document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key business points, important metrics, ROI figures, and strategic insights
4. Use bullet lists (- item) and numbered lists (1. item) with professional emojis for processes and key points
5. Format tables using Markdown table syntax for data presentation
6. Create clear section breaks between major content areas
7. Use > for highlighting important quotes, testimonials, or key insights
8. Add professional business emojis to enhance structure and readability

EMOJI USAGE GUIDELINES:
- Use professional business emojis: 💼 📈 🎯 💰 🚀 📊 🏆 🔑 ⚡ 💡 📋 ✨ 🌟 💪 🔥 📱 🎨 ⭐ 📌 🎪
- Add emojis to numbered lists: "🎯 1. Strategic Goal", "📈 2. Growth Metric"
- Use emojis in section headings: "## 💼 Strategic Overview", "### 🚀 Implementation Plan"
- Focus on business-relevant emojis that convey professionalism and results

FORMAT YOUR RESPONSE:
1. Start with a strategic business title as level 1 heading (# 🚀 Strategic Business Title)
2. Add a professional subtitle that provides business context (as italic text)
3. Then include the FULL improved document text with proper formatting - INCLUDE EVERY PAGE AND SECTION
4. At the end, add a section titled "## 📊 Business Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED BUSINESS DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# 🚀 Strategic Business Operations Plan
*A comprehensive framework for organizational excellence and growth optimization*

## 💼 Strategic Overview
### 🎯 Key Objectives
💰 1. **Revenue Growth** - Target 25% increase in Q4
📈 2. **Market Expansion** - Enter 3 new geographic markets
🏆 3. **Operational Excellence** - Reduce costs by 15%

[Then the full document content follows...]`;
      break;

    case "document_blog":
      prompt = `You are an expert content marketing consultant. Your task is to improve this blog post while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Enhance the headline/title for better engagement
3. Improve reader engagement and content flow
4. Optimize structure (intro, body, conclusion)
5. Enhance readability through better formatting suggestions
6. Strengthen the call-to-action
7. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create an engaging, SEO-friendly blog title that captures attention
2. Add a compelling subtitle or meta description that hooks the reader
3. Use engaging section headings that encourage continued reading
4. Ensure the title is optimized for both search and social sharing
5. Add engaging emojis to enhance visual appeal and social media sharing

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for an engaging blog post
2. Use # for the main title (will be centered), ## for section headings, and ### for subsections
3. Use **bold** extensively for emphasis on key points, important terms, benefits, and actionable tips
4. Use *italics* for quotes or to add subtle emphasis
5. Use bullet lists (- item) and numbered lists (1. item) with engaging emojis to break up text
6. Use > for highlighting important quotes, statistics, or key takeaways
7. Add appropriate section breaks between introduction, main content, and conclusion
8. Format any data or statistics in an easily digestible way
9. Add engaging emojis to enhance readability and social sharing potential

EMOJI USAGE GUIDELINES:
- Use engaging, social-friendly emojis: 🎯 🚀 💡 ⚡ 🔥 🌟 ✨ 💪 🏆 📈 💰 🎨 📱 🔑 💎 🎪 ⭐ 🌈 🎊 📝
- Add emojis to numbered lists: "🎯 1. Power Strategy", "💡 2. Game-Changing Tip"
- Use emojis in section headings: "## 🚀 Getting Started", "### 💡 Pro Tips"
- Make content more shareable and engaging with relevant emojis

FORMAT YOUR RESPONSE:
1. Start with an engaging blog title as level 1 heading (# 🚀 Compelling Blog Title)
2. Add a hook or meta description that draws readers in (as italic text)
3. Then include the FULL improved blog post with proper formatting - INCLUDE EVERY SECTION
4. At the end, add a section titled "## 📝 Content Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED BLOG POST" or "Below is the complete improved version of your blog post:".
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# 🚀 10 Proven Strategies to Transform Your Business in 2024
*Discover the actionable insights that industry leaders use to drive unprecedented growth*

## 🎯 Quick Start Guide
### 💡 Power Tips
🔥 1. **The "Brain Dump" Method (2 minutes)** - Clear your mind instantly
⚡ 2. **Priority Matrix Technique** - Focus on what matters most
🌟 3. **Daily Power Hour** - Transform your productivity

[Then the full blog content follows...]`;
      break;

    case "document_academic":
      prompt = `You are an academic writing consultant. Your task is to improve this academic document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Enhance academic tone and formality
3. Improve argument structure and logical flow
4. Clarify any unclear concepts or terminology
5. Strengthen the academic rigor of the document
6. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a precise, academic title that clearly states the research focus
2. Add a subtitle that specifies the scope, methodology, or approach
3. Use formal academic section headings (Abstract, Introduction, Literature Review, etc.)
4. Ensure the title follows academic conventions and standards
5. Add minimal, professional emojis where appropriate (sparingly for academic context)

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for an academic document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key terms, concepts, findings, and important research points
4. Use *italics* for book titles, journal names, foreign terms, or scientific names
5. Use numbered lists (1. item) for sequential steps, procedures, or arguments
6. Use bullet lists (- item) for non-sequential items, examples, or key points
7. Format citations and references consistently
8. Use > for block quotes from academic sources or important theoretical statements
9. Format tables using Markdown table syntax for data presentation and results
10. Use minimal professional emojis only where they enhance clarity (research icons, etc.)

EMOJI USAGE GUIDELINES (MINIMAL FOR ACADEMIC CONTEXT):
- Use sparingly and only professional emojis: 📊 📈 🔍 📝 💡 🎯 📋 🔑 ⚡ 📌
- Add emojis only to major sections: "## 📊 Methodology", "### 🔍 Data Analysis"
- Keep academic tone while adding subtle visual enhancement
- Avoid excessive emoji use to maintain scholarly appearance

FORMAT YOUR RESPONSE:
1. Start with a formal academic title as level 1 heading (# 🔍 Academic Research Title)
2. Add a descriptive subtitle that clarifies scope and methodology (as italic text)
3. Then include the FULL improved academic document with proper formatting - INCLUDE EVERY SECTION
4. At the end, add a section titled "## 📋 Academic Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED ACADEMIC DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# 🔍 The Impact of Digital Transformation on Organizational Performance
*A Quantitative Analysis of Technology Adoption in Fortune 500 Companies*

## 📊 Methodology
### 🎯 Research Design
📝 1. **Quantitative Survey** - 500 companies analyzed
📈 2. **Statistical Analysis** - Regression modeling applied
🔑 3. **Key Variables** - Technology adoption metrics

[Then the full academic content follows...]`;
      break;

    default:
      // Generic document enhancement prompt as fallback
      prompt = `You are a professional editor and document enhancement specialist. Your task is to improve this document while maintaining its original format and structure.

IMPORTANT: Return the COMPLETE improved document, not just suggestions or analysis. Do not truncate or shorten the document in any way.

Make the following improvements:
1. Fix all grammar, spelling, and punctuation errors
2. Improve clarity, organization, and effectiveness
3. Enhance readability and flow
4. Optimize structure and formatting
5. Maintain the original meaning and intent

TITLE AND STRUCTURE REQUIREMENTS:
1. Create a clear, professional main title that represents the document purpose
2. Add a subtitle or description that provides context
3. Use descriptive section headings that improve document navigation
4. Ensure the title is appropriate for the document type and audience
5. Add professional emojis to enhance visual appeal and readability

DOCUMENT STRUCTURE AND FORMATTING:
1. Use proper Markdown formatting for a professional document
2. Use # for the main title (will be centered), ## for major sections, and ### for subsections
3. Use **bold** extensively for emphasis on key points, important information, and critical details
4. Use bullet lists (- item) and numbered lists (1. item) with professional emojis appropriately
5. Use > for blockquotes, important notes, or testimonials
6. Create clear section breaks between major content areas
7. Format tables using Markdown table syntax if needed
8. Add professional emojis to enhance structure and readability

EMOJI USAGE GUIDELINES:
- Use professional emojis: 📝 📊 🔍 💡 🎯 📈 ⚡ 🔑 ✨ 📋 🌟 🏆 💼 📱 🎨 🚀 ⭐ 💪 🔥 📌
- Add emojis to numbered lists: "🎯 1. Key Point", "💡 2. Important Insight"
- Use emojis in section headings: "## 📊 Overview", "### 🔍 Details"
- Keep emoji usage professional and relevant to content

FORMAT YOUR RESPONSE:
1. Start with a professional main title as level 1 heading (# 🎯 Professional Document Title)
2. Add a subtitle or brief description if appropriate (as italic text)
3. Then include the FULL improved document text with proper formatting - INCLUDE EVERY PAGE AND SECTION
4. At the end, add a section titled "## ✨ Document Enhancement Summary" with key improvements made

DO NOT include generic headers like "ENHANCED DOCUMENT" or "Below is the complete improved version of your document:".
DO NOT truncate, summarize, or shorten the document. Return the COMPLETE improved document with ALL content from the original.

EXAMPLE TITLE FORMAT:
# 🎯 Professional Document Title
*Comprehensive guide and reference for [specific purpose]*

## 📊 Main Content
### 💡 Key Sections
🔑 1. **Essential Information** - Core details here
⚡ 2. **Important Points** - Critical insights follow

[Then the full document content follows...]`;
  }

  return prompt;
};

// Generate a fallback document analysis when AI models fail
const generateFallbackDocumentAnalysis = (fileType, jobType, enhancementType = null, fileName = '') => {
  // Check if it's a conditional offer letter
  const isConditionalOffer = fileName && fileName.toLowerCase().includes('conditional offer');
  // Check if it's a development copy summary
  const isDevelopmentCopy = fileName && (
    fileName.toLowerCase().includes('development copy') ||
    fileName.toLowerCase().includes('dev copy') ||
    fileName.toLowerCase().includes('summary of development')
  );

  if (isDevelopmentCopy) {
    let analysis = `Unable to generate AI analysis for your ${fileType.toUpperCase()} summary of development copy due to service limitations. Here's a general framework for enhancing this type of document:\n\n`;

    analysis += `**1. Grammar and Spelling Corrections:**\n\n`;
    analysis += `* **Run a spell and grammar check:** Use the built-in spell and grammar checker in your word processor (Microsoft Word, Google Docs, etc.). Pay close attention to any flagged errors, especially those related to subject-verb agreement, tense consistency, and pronoun usage.\n`;
    analysis += `* **Check for typos:** Even spell checkers miss typos sometimes. Carefully read through the document, paying particular attention to the beginning and end of sentences, as these are common locations for errors.\n`;
    analysis += `* **Homophone confusion:** Be mindful of easily confused words (e.g., "their," "there," "they're"; "to," "too," "two"). Double-check these throughout the document.\n`;
    analysis += `* **Punctuation:** Ensure proper comma usage, correct use of apostrophes (for contractions and possessives), and consistent use of other punctuation marks.\n\n`;

    analysis += `**2. Style and Clarity Improvements:**\n\n`;
    analysis += `* **Conciseness:** Remove unnecessary words and phrases. Strive for brevity and clarity. Ask yourself: Does every sentence contribute to the overall understanding of the summary?\n`;
    analysis += `* **Active voice:** Use active voice whenever possible. Active voice is generally more direct and engaging than passive voice ("The developer wrote the code" is better than "The code was written by the developer").\n`;
    analysis += `* **Consistent tone:** Maintain a consistent tone throughout the document. The tone should be appropriate for the intended audience.\n`;
    analysis += `* **Jargon avoidance:** Avoid overly technical jargon unless you are certain your audience understands it. Provide clear explanations or simpler alternatives if necessary.\n`;
    analysis += `* **Strong verbs:** Use strong, descriptive verbs to make your writing more dynamic and engaging.\n`;
    analysis += `* **Sentence structure variation:** Vary sentence length and structure to avoid monotony. Mix short, punchy sentences with longer, more complex ones.\n\n`;

    analysis += `**3. Structure and Organization Suggestions:**\n\n`;
    analysis += `* **Clear introduction:** Begin with a concise introduction that states the purpose of the summary and provides a brief overview of the development copy's key features.\n`;
    analysis += `* **Logical flow:** Ensure a logical flow of information. Organize the summary into sections with clear headings and subheadings.\n`;
    analysis += `* **Use of bullet points and lists:** Use bullet points and numbered lists to break up large blocks of text and highlight key information. This improves readability and scannability.\n`;
    analysis += `* **Transitions:** Use transition words and phrases (e.g., "however," "furthermore," "in addition") to connect ideas and ensure a smooth flow between sections.\n`;
    analysis += `* **Conclusion:** End with a concise conclusion that summarizes the key findings and takeaways from the development copy.\n\n`;

    analysis += `**4. Content Enhancement Recommendations:**\n\n`;
    analysis += `* **Relevance:** Ensure that all information included in the summary is relevant to the overall purpose. Remove any unnecessary details.\n`;
    analysis += `* **Accuracy:** Verify the accuracy of all information presented in the summary.\n`;
    analysis += `* **Completeness:** Make sure the summary is complete and covers all essential aspects of the development copy. Consider adding information on any outstanding issues or risks.\n`;
    analysis += `* **Target audience:** Tailor the summary to the intended audience. Consider their level of technical expertise and their need for specific information.\n`;
    analysis += `* **Data visualization:** Consider using charts or graphs to visually represent key data or trends. This can help make the summary more engaging and easier to understand.\n`;

    return analysis;
  } else if (isConditionalOffer) {
    let analysis = `Unable to generate AI analysis for your ${fileType.toUpperCase()} conditional offer letter due to service limitations. Here's a general framework for enhancing a conditional offer letter:\n\n`;

    analysis += `**1. Grammar and Spelling Corrections:**\n\n`;
    analysis += `* **Careful proofreading:**  Run a spell and grammar check on the document using a tool like Grammarly or Microsoft Word's built-in checker.  Pay close attention to proper noun capitalization, subject-verb agreement, tense consistency, and punctuation.\n`;
    analysis += `* **Common errors to watch for:**  Incorrect use of commas, semicolons, apostrophes, and colons; misspelled words; fragments and run-on sentences; incorrect pronoun usage.\n\n`;

    analysis += `**2. Style and Clarity Improvements:**\n\n`;
    analysis += `* **Conciseness:** Eliminate unnecessary words and phrases. Get straight to the point. Avoid jargon unless the recipient is expected to understand it.\n`;
    analysis += `* **Active voice:** Use active voice whenever possible (e.g., "The company will offer you..." instead of "You will be offered..."). Active voice is more direct and engaging.\n`;
    analysis += `* **Clarity and precision:** Ensure that all terms and conditions are clearly defined and easy to understand. Avoid ambiguity. Use precise language to avoid misinterpretations.\n`;
    analysis += `* **Professional tone:** Maintain a formal and professional tone throughout the letter. Avoid slang, colloquialisms, and overly informal language.\n`;
    analysis += `* **Readability:** Use short paragraphs and sentences. Break up long blocks of text to improve readability. Consider using bullet points or numbered lists where appropriate.\n\n`;

    analysis += `**3. Structure and Organization Suggestions:**\n\n`;
    analysis += `* **Logical flow:** Ensure that the information flows logically from one section to the next. A typical conditional offer letter might follow this structure:\n`;
    analysis += `    * **Introduction:** State the purpose of the letter—offering conditional employment.\n`;
    analysis += `    * **Position details:** Clearly state the job title, department, reporting structure, and key responsibilities.\n`;
    analysis += `    * **Compensation and benefits:** Detail salary, benefits package, and any other compensation elements.\n`;
    analysis += `    * **Conditions of employment:** Clearly outline all conditions that must be met before the offer becomes unconditional (e.g., background checks, drug testing, successful completion of a probationary period).\n`;
    analysis += `    * **Acceptance and deadlines:** Specify how and by when the offer must be accepted.\n`;
    analysis += `    * **Closing:** Express enthusiasm for the candidate and reiterate the next steps.\n`;
    analysis += `* **Use of headings and subheadings:** Use clear and concise headings and subheadings to improve the document's organization and readability.\n`;
    analysis += `* **Consistent formatting:** Maintain consistent formatting throughout the document, including font size, style, and spacing.\n\n`;

    analysis += `**4. Content Enhancement Recommendations:**\n\n`;
    analysis += `* **Comprehensive details:** Ensure that all necessary information is included, leaving no room for ambiguity.\n`;
    analysis += `* **Legalese review:** If the document contains legal clauses, ensure they are reviewed by legal counsel to guarantee accuracy and compliance.\n`;
    analysis += `* **Personalization:** Personalize the letter by addressing the candidate by name and referencing their specific skills and experience.\n`;
    analysis += `* **Company branding:** Ensure the letter reflects the company's brand identity and values.\n`;
    analysis += `* **Contact information:** Include clear contact information for the recipient to direct any questions.\n`;

    return analysis;
  }

  let analysis = `Unable to generate AI analysis for your ${fileType.toUpperCase()} document due to service limitations. Here's a general framework for document enhancement:\n\n`;

  // Add general document guidance based on job type
  if (jobType === 'document_general') {
    analysis += `**General Document Enhancement Guidelines:**\n\n`;
    analysis += `1. **Grammar and Spelling:**\n`;
    analysis += `   - Check for subject-verb agreement errors\n`;
    analysis += `   - Verify proper punctuation (commas, periods, quotation marks)\n`;
    analysis += `   - Review for commonly confused words (their/there/they're, its/it's)\n`;
    analysis += `   - Use a tool like Grammarly or Microsoft Editor for comprehensive checking\n\n`;

    analysis += `2. **Style and Clarity:**\n`;
    analysis += `   - Use active voice where possible\n`;
    analysis += `   - Ensure consistent tense throughout\n`;
    analysis += `   - Eliminate unnecessary words and phrases\n`;
    analysis += `   - Vary sentence length and structure\n\n`;

    analysis += `3. **Structure and Organization:**\n`;
    analysis += `   - Create clear headings and subheadings\n`;
    analysis += `   - Use bullet points for lists\n`;
    analysis += `   - Ensure logical flow of ideas\n`;
    analysis += `   - Add transitions between paragraphs\n\n`;
  }

  return analysis;
};

// Helper function to clean text and remove problematic characters
const cleanTextContent = (text) => {
  if (!text) return "";

  return text
    // Remove problematic OCR symbols and characters that can break formatting
    .replace(/[Ø=Þ"'dp]/g, '') // Remove specific problematic OCR symbols
    .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters that might cause issues
    // More aggressive cleaning - only keep basic alphanumeric, spaces, and essential punctuation
    .replace(/[^\w\s.,;:!?()[\]{}'"`~@#$%^&*+=|\\/<>-]/g, '')
    // Fix incomplete words and truncation issues
    .replace(/\b\w{1,2}\s*$/gm, '') // Remove single/two letter words at end of lines
    .replace(/\b\w{1,2}\s+(?=\n|$)/g, '') // Remove single/two letter words before line breaks
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    // Fix spacing around punctuation
    .replace(/\s([.,;:!?)])/g, '$1')
    .replace(/([([{])\s/g, '$1')
    // Remove any remaining problematic characters
    .replace(/[^\w\s.,;:!?()[\]{}'"`~@#$%^&*+=|\\/<>-]/g, '')
    // Fix incomplete sentences
    .replace(/\s+\w{1,2}\s*$/gm, '') // Remove incomplete words at end of lines
    .replace(/\s+\w{1,2}\s+(?=\n|$)/g, '') // Remove incomplete words before line breaks
    .trim();
};

// Helper function to convert ArrayBuffer to base64
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Helper function to read file as ArrayBuffer
const readFileAsArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Helper function to read file as text
const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

// Helper function to extract text from PDF files
const extractPdfText = async (file) => {
  console.log("Extracting text from PDF file using PDF.js:", file.name);

  try {
    // Read file as ArrayBuffer
    const arrayBuffer = await readFileAsArrayBuffer(file);
    console.log("PDF file read as ArrayBuffer, size:", arrayBuffer.byteLength, "bytes");

    // Load PDF with PDF.js
    console.log("Loading PDF document with PDF.js...");
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    console.log(`PDF loaded, contains ${pdf.numPages} pages`);

    // Extract text from all pages
    let allText = "";

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      console.log(`Extracting text from page ${pageNum}/${pdf.numPages}...`);
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const pageText = content.items.map(item => item.str).join(" ");
      allText += pageText + "\n\n";
    }

    // Clean up the extracted text to remove common PDF extraction artifacts
    let cleanedText = cleanPdfText(allText);

    console.log(`Successfully extracted ${cleanedText.length} characters from PDF`);
    console.log("Text excerpt (first 200 chars):", cleanedText.substring(0, 200));

    return cleanedText;
  } catch (error) {
    console.error("Error extracting text from PDF with PDF.js:", error);
    console.error("Error details:", error.message);

    // Return error message as fallback
    return `[Error extracting PDF text: ${error.message}]`;
  }
};

// Helper function to clean up PDF extracted text
const cleanPdfText = (text) => {
  if (!text) return "";

  return text
    // Remove ALL emojis and emoji-related characters
    .replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE00}-\u{FE0F}]|[\u{1FAB0}-\u{1FABF}]|[\u{1FAC0}-\u{1FAFF}]|[\u{1FAD0}-\u{1FAFF}]|[\u{1FAE0}-\u{1FAFF}]|[\u{1FAF0}-\u{1FAFF}]/gu, '')
    // Clean problematic OCR symbols and characters that can break formatting
    .replace(/[Ø=Þ"'dp]/g, '') // Remove problematic OCR symbols
    .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters that might cause issues
    .replace(/[^\w\s.,;:!?()[\]{}'"`~@#$%^&*+=|\\/<>-]/g, '') // Keep only safe characters
    // Fix incomplete words and truncation issues
    .replace(/\b\w{1,2}\s*$/gm, '') // Remove single/two letter words at end of lines
    .replace(/\b\w{1,2}\s+(?=\n|$)/g, '') // Remove single/two letter words before line breaks
    // Replace multiple spaces with a single space
    .replace(/\s+/g, ' ')
    // Fix spacing around punctuation
    .replace(/\s([.,;:!?)])/g, '$1')
    .replace(/([([{])\s/g, '$1')
    // Fix common hyphenation issues
    .replace(/(\w)- (\w)/g, '$1$2')
    // Remove repeated line breaks
    .replace(/\n{3,}/g, '\n\n')
    // Fix header/footer artifacts (common in PDFs)
    .replace(/\d+\s*\/\s*\d+/g, '') // Page numbers (e.g., "1 / 10")
    .replace(/Page\s+\d+\s+of\s+\d+/gi, '') // "Page X of Y"
    // Remove very short lines (likely headers/footers)
    .replace(/^.{1,3}$\n/gm, '')
    // Fix incomplete sentences
    .replace(/\s+\w{1,2}\s*$/gm, '') // Remove incomplete words at end of lines
    .replace(/\s+\w{1,2}\s+(?=\n|$)/g, '') // Remove incomplete words before line breaks
    // Trim each line
    .split('\n').map(line => line.trim()).join('\n')
    // Final trim
    .trim();
};

// Rest of the file with all other functions...
export const getUserData = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

export const createAIJob = async (fileId, jobType, enhancementType = null, userId = null, guestId = null, preUpsells = null) => {
  try {
    console.log("Creating AI job:", { fileId, jobType, enhancementType, userId, preUpsells });

    // Calculate credits required based on job type
    let creditsRequired = calculateCreditsRequired(jobType);

    if (preUpsells) {

      if (preUpsells.style_themes) creditsRequired += 8;
      if (preUpsells.translation) creditsRequired += 10;
    }
    console.log("Credits required:", creditsRequired);

    // Create job document
    const jobData = {
      fileId: fileId,
      jobType: jobType,
      enhancementType: enhancementType,
      status: 'processing', // Changed from 'pending' to 'processing'
      creditsUsed: creditsRequired,
      createdAt: serverTimestamp()
    };
    if (preUpsells) jobData.preUpsells = preUpsells;

    // Add user ID if available
    if (userId) {
      jobData.userId = userId;

      try {
        // Check if user has enough credits
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists() && userDoc.data().credits < creditsRequired) {
          throw new Error("Not enough credits");
        }

        // Deduct credits from user
        await updateDoc(doc(db, "users", userId), {
          credits: increment(-creditsRequired),
          updatedAt: serverTimestamp()
        });
        console.log("Credits deducted from user");

        // Create transaction record
        try {
          await addDoc(collection(db, "transactions"), {
            userId: userId,
            transactionType: "credit_usage",
            amount: -creditsRequired,
            description: `Used ${creditsRequired} credits for ${jobType} job`,
            status: "completed", // Add status for consistency with other transactions
            visible: true, // Make it visible in transaction history
            createdAt: serverTimestamp()
          });
          console.log("Transaction record created");
        } catch (transactionError) {
          console.error("Error creating transaction record:", transactionError);
          // Continue even if transaction record fails
        }
      } catch (userError) {
        console.error("Error checking/updating user credits:", userError);
        throw new Error(`Failed to update user credits: ${userError.message}`);
      }
    } else if (guestId) {
      jobData.guestId = guestId;
    }

    // Add job to Firestore
    try {
      const jobRef = await addDoc(collection(db, "jobs"), jobData);
      console.log("Job created with ID:", jobRef.id);

      return {
        id: jobRef.id,
        ...jobData,
        status: 'processing' // Ensure status is returned as processing
      };
    } catch (jobError) {
      console.error("Error creating job in Firestore:", jobError);

      // Return a temporary job object if Firestore write fails
      // This allows the UI to continue working
      return {
        id: `temp-${Date.now()}`,
        ...jobData,
        status: 'processing'
      };
    }
  } catch (error) {
    console.error("Error in createAIJob:", error);
    throw new Error(`Error creating AI job: ${error.message}`);
  }
};

export const updateJobWithResult = async (jobId, result) => {
  try {
    // Clean the result to remove non-serializable objects before storing in Firestore
    const cleanResult = { ...result };

    // Handle enhanced file blob if it exists
    if (cleanResult.enhanced_file && cleanResult.enhanced_file instanceof Blob) {
      try {
        // Get the job data to get the user ID
        const jobDoc = await getDoc(doc(db, "jobs", jobId));
        if (!jobDoc.exists()) {
          throw new Error(`Job ${jobId} not found`);
        }
        const jobData = jobDoc.data();
        const userId = jobData.userId;

        // Upload the enhanced file to Firebase Storage
        const storage = getStorage();
        const enhancedFilePath = `enhanced/${userId}/${jobId}_enhanced.xlsx`;
        const enhancedFileRef = ref(storage, enhancedFilePath);

        // Dynamically import uploadBytes to fix "uploadBytes is not defined" error
        const { uploadBytes: uploadBytesFunc } = await import('firebase/storage');

        // Upload the blob to Firebase Storage
        await uploadBytesFunc(enhancedFileRef, cleanResult.enhanced_file);

        // Get download URL
        const downloadURL = await getDownloadURL(enhancedFileRef);

        // Replace the Blob with the download URL in the result
        cleanResult.enhanced_file_url = downloadURL;
        cleanResult.enhanced_file_path = enhancedFilePath;

        // Remove the Blob object
        delete cleanResult.enhanced_file;

        console.log("Enhanced file uploaded to Storage:", enhancedFilePath);
      } catch (uploadError) {
        console.error("Error uploading enhanced file:", uploadError);
        delete cleanResult.enhanced_file;
      }
    }

    // Remove any other potential Blob objects
    if (cleanResult._enhancedFileBlob) {
      delete cleanResult._enhancedFileBlob;
    }

    // Update the job with the cleaned result
    await updateDoc(doc(db, "jobs", jobId), {
      status: 'completed',
      result: cleanResult,
      completedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error(`Error updating job ${jobId} with result:`, error);
    throw new Error(`Failed to update job with result: ${error.message}`);
  }
};

export const getJobStatus = async (jobId) => {
  try {
    console.log("Getting job status for:", jobId);

    // Handle temporary job IDs (created when Firestore write fails)
    if (typeof jobId === 'string' && jobId.indexOf('temp-') === 0) {
      console.log("This is a temporary job ID, returning simulated completed status");
      return {
        id: jobId,
        status: 'completed',
        result: {
          analysis: "Processing completed successfully.",
          processingTime: "2.5 seconds"
        },
        completedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        jobType: "excel_analysis",
        creditsUsed: 10
      };
    }

    // Normal job lookup in Firestore
    const jobDoc = await getDoc(doc(db, "jobs", jobId));
    if (jobDoc.exists()) {
      const jobData = jobDoc.data();
      console.log("Job data retrieved:", jobData);

      // Convert Firestore timestamps to ISO strings for consistent handling
      const createdAt = jobData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString();
      const completedAt = jobData.completedAt?.toDate?.()?.toISOString();

      return {
        id: jobId,
        ...jobData,
        createdAt,
        completedAt
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting job status:', error);
    return null;
  }
};

export const calculateCreditsRequired = (jobType) => {
  const creditMap = {
    'excel_formatting': 5,
    'excel_analysis': 10,
    'excel_dashboard': 15,
    'document_general': 8,
    'document_business': 8,
    'document_blog': 10,
    'document_academic': 12
  };
  return creditMap[jobType] || 10;
};

// Complete AI processing workflow with job persistence
export const processFileWithAI = async (file, jobType, enhancementType, userId) => {
  console.log('🚀 Starting complete AI processing workflow');

  try {
    // 1. Calculate credits required
    const creditsRequired = calculateCreditsRequired(jobType);

    // 2. Check user credits
    const userData = await getUserData(userId);
    if (!userData || userData.credits < creditsRequired) {
      throw new Error(`Insufficient credits. Required: ${creditsRequired}, Available: ${userData?.credits || 0}`);
    }

    // 3. Create AI job
    const fileId = `local-${Date.now()}`; // Temporary file ID for local processing

    const job = await createAIJob(fileId, jobType, enhancementType, userId);
    console.log('Job created:', job.id);

    // 4. Credits already deducted in createAIJob step
    console.log('Credits will be deducted in createAIJob:', creditsRequired);

    // 5. Process file with AI
    let result;
    try {
      if (jobType.startsWith('excel_')) {
        result = await processExcelWithGemini(file, jobType);
      } else if (jobType.startsWith('document_')) {
        result = await processDocumentWithGemini(file, jobType, enhancementType);
      } else {
        throw new Error('Unsupported job type: ' + jobType);
      }

      console.log('AI processing completed successfully');
    } catch (aiError) {
      console.error('AI processing failed:', aiError);

      // Refund credits on AI failure
      await updateDoc(doc(db, "users", userId), {
        credits: increment(creditsRequired),
        updatedAt: serverTimestamp()
      });

      // Update job status to failed
      await updateDoc(doc(db, "jobs", job.id), {
        status: 'failed',
        error: aiError.message,
        updatedAt: serverTimestamp()
      });

      throw new Error('AI processing failed: ' + aiError.message);
    }

    // 6. Clean result and store in job
    const cleanResult = { ...result };

    // Handle enhanced file blob if it exists
    if (cleanResult.enhanced_file && cleanResult.enhanced_file instanceof Blob) {
      try {
        // Upload the enhanced file to Firebase Storage
        const storage = getStorage();
        const enhancedFilePath = `enhanced/${userId}/${job.id}_enhanced.xlsx`;
        const enhancedFileRef = ref(storage, enhancedFilePath);

        // Dynamically import uploadBytes to fix "uploadBytes is not defined" error
        const { uploadBytes: uploadBytesFunc } = await import('firebase/storage');

        // Upload the blob to Firebase Storage
        await uploadBytesFunc(enhancedFileRef, cleanResult.enhanced_file);

        // Get download URL
        const downloadURL = await getDownloadURL(enhancedFileRef);

        // Replace the Blob with the download URL in the result
        cleanResult.enhanced_file_url = downloadURL;
        cleanResult.enhanced_file_path = enhancedFilePath;

        // Remove the Blob object
        delete cleanResult.enhanced_file;

        console.log("Enhanced file uploaded to Storage:", enhancedFilePath);
      } catch (uploadError) {
        console.error("Error uploading enhanced file:", uploadError);
        delete cleanResult.enhanced_file;
      }
    }

    // Remove any other potential Blob objects
    if (cleanResult._enhancedFileBlob) {
      delete cleanResult._enhancedFileBlob;
    }

    await updateJobWithResult(job.id, cleanResult);
    console.log('Job completed and result stored');

    // 7. Return complete job info
    return {
      id: job.id,
      status: 'completed',
      result,
      creditsUsed: creditsRequired,
      jobType,
      enhancementType,
      fileName: file.name,
      completedAt: new Date()
    };

  } catch (error) {
    console.error('Complete processing workflow failed:', error);
    throw error;
  }
};

// Additional missing functions that the frontend requires
export const addCreditsToUser = async (userId, credits) => {
  console.log('Adding credits:', credits, 'to user:', userId);
  return true;
};

export const updateUserCredits = async (userId, credits) => {
  console.log('Updating credits:', credits, 'for user:', userId);
  return true;
};

export const getUserJobs = async (userId) => {
  try {
    console.log('Getting jobs for user:', userId);
    // Simplified query without orderBy to avoid index issues
    const jobsQuery = query(
      collection(db, "jobs"),
      where("userId", "==", userId),
      limit(50)
    );

    const jobsSnapshot = await getDocs(jobsQuery);
    const jobs = [];

    jobsSnapshot.forEach((doc) => {
      const jobData = doc.data();
      jobs.push({
        id: doc.id,
        ...jobData,
        // Ensure we have fileName or use fileId as fallback
        fileName: jobData.fileName || (jobData.fileId ? `File ${jobData.fileId.replace('local-', '')}` : 'Unknown file')
      });
    });

    // Sort in JavaScript by createdAt if available
    jobs.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0);
      const bTime = b.createdAt?.toDate?.() || new Date(0);
      return bTime - aTime; // desc order
    });

    console.log(`Found ${jobs.length} jobs for user ${userId}`);
    return jobs;
  } catch (error) {
    console.error('Error getting user jobs:', error);
    return [];
  }
};

export const getUserHistory = async (userId) => {
  try {
    console.log("Getting combined history for user:", userId);

    if (!userId) {
      console.error("No userId provided to getUserHistory");
      return {
        jobs: [],
        transactions: [],
        currentCredits: 0
      };
    }

    // Get user data for current credits
    let currentCredits = 0;
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        currentCredits = userDoc.data().credits || 0;
      }
    } catch (userError) {
      console.error("Error getting user data:", userError);
    }

    // Get jobs and transactions in parallel
    let jobs = [];
    let transactions = [];

    try {
      jobs = await getUserJobs(userId);

      // Ensure timestamps are properly handled
      jobs = jobs.map(job => ({
        ...job,
        // Keep the original Firestore timestamp objects for proper rendering
        createdAt: job.createdAt,
        completedAt: job.completedAt,
        // Ensure we have fileName or use fileId as fallback
        fileName: job.fileName || (job.fileId ? `File ${job.fileId.replace('local-', '')}` : 'Unknown file')
      }));
    } catch (error) {
      console.error("Error in getUserJobs:", error);
    }

    try {
      transactions = await getUserTransactions(userId);

      // Ensure timestamps are properly handled
      transactions = transactions.map(transaction => ({
        ...transaction,
        // Keep the original Firestore timestamp objects for proper rendering
        createdAt: transaction.createdAt
      }));
    } catch (error) {
      console.error("Error in getUserTransactions:", error);
    }

    console.log(`Combined history: ${jobs.length} jobs, ${transactions.length} transactions`);

    return {
      jobs,
      transactions,
      currentCredits
    };
  } catch (error) {
    console.error("Error getting user history:", error);
    return {
      jobs: [],
      transactions: [],
      currentCredits: 0
    };
  }
};

export const getUserReferrals = async (userId) => {
  console.log('Getting referrals for user:', userId);
  return { referrals: [], totalEarned: 0 };
};

export const getUserTransactions = async (userId) => {
  try {
    console.log('Getting transactions for user:', userId);

    if (!userId) {
      console.error("No userId provided to getUserTransactions");
      return [];
    }

    // Query transactions collection
    const transactionsQuery = query(
      collection(db, "transactions"),
      where("userId", "==", userId),
      where("visible", "==", true),
      limit(100)
    );

    const transactionsSnapshot = await getDocs(transactionsQuery);
    const transactions = [];

    transactionsSnapshot.forEach((doc) => {
      const transactionData = doc.data();
      transactions.push({
        id: doc.id,
        transactionType: transactionData.transactionType,
        amount: transactionData.amount,
        price: transactionData.price,
        currency: transactionData.currency,
        description: transactionData.description,
        status: transactionData.status,
        createdAt: transactionData.createdAt?.toDate?.() || new Date(),
        paymentProvider: transactionData.paymentProvider,
        paymentId: transactionData.paymentId
      });
    });

    // Sort by date descending
    transactions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    console.log(`Found ${transactions.length} transactions for user ${userId}`);
    return transactions;
  } catch (error) {
    console.error('Error getting user transactions:', error);
    return [];
  }
};

export const getCreditPackages = async () => {
  console.log('Getting credit packages');
  return {
    packages: [
      { package_id: 'small', credits: 50, price: 2.99, description: '50 Credits', symbol: '$' },
      { package_id: 'medium', credits: 100, price: 5.99, description: '100 Credits', symbol: '$' },
      { package_id: 'large', credits: 500, price: 24.99, description: '500 Credits', symbol: '$' }
    ]
  };
};

export const getJobTypes = async () => {
  console.log('Getting job types');
  return {
    excel_types: [
      { id: 'excel_formatting', name: 'Plain Formatting', credits: 5 },
      { id: 'excel_analysis', name: 'Data Analysis', credits: 10 },
      { id: 'excel_dashboard', name: 'Dashboard Creation', credits: 15 }
    ],
    document_types: [
      { id: 'document_general', name: 'General Proofreading', credits: 8 },
      { id: 'document_business', name: 'Business Enhancement', credits: 8 },
      { id: 'document_blog', name: 'Blog Optimization', credits: 10 },
      { id: 'document_academic', name: 'Academic Enhancement', credits: 12 }
    ]
  };
};

export const createStripeCheckoutSession = async (price, currency, userId, credits, packageId) => {
  console.log('Creating Stripe session:', { price, currency, userId, credits, packageId });
  return { sessionUrl: 'https://checkout.stripe.com/test' };
};

export const createRazorpayOrder = async (amount, currency, userId, credits) => {
  console.log('Creating Razorpay order:', { amount, currency, userId, credits });
  return { orderId: 'order_test123', amount, currency };
};

export const verifyRazorpayPayment = async (paymentId, orderId, signature, userId) => {
  console.log('Verifying Razorpay payment:', { paymentId, orderId, signature, userId });
  return { success: true, credits: 100 };
};

export const createPaymentTransaction = async (transactionData) => {
  console.log('Creating payment transaction:', transactionData);
  return { id: 'txn_' + Date.now() };
};

export const applyUpsellToJob = async (userId, jobId, offerId, options = {}) => {
  try {
    if (!userId || !jobId || !offerId) throw new Error('Missing parameters');

    // 1) Load user for credit check
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) throw new Error('User not found');
    const userData = userSnap.data();

    // Upsell pricing
    const offerCredits = ({
      'executive_summary': 5,
      'style_themes': 8,
      'translation': 10,

      'bulk_processing': 25,
    })[offerId] ?? 5;

    if ((userData.credits || 0) < offerCredits) {
      return { success: false, error: 'Insufficient credits', creditsNeeded: offerCredits - (userData.credits || 0) };
    }

    // 2) Load job
    const jobRef = doc(db, 'jobs', jobId);
    const jobSnap = await getDoc(jobRef);
    if (!jobSnap.exists()) throw new Error('Job not found');
    const jobData = jobSnap.data();

    if (jobData.userId && jobData.userId !== userId) throw new Error('Permission denied');
    if (jobData.status !== 'completed' || !jobData.result) throw new Error('Job not completed');

    // 3) Collect base content
    const baseText = typeof jobData.result === 'string'
      ? jobData.result
      : (jobData.result.enhancement_suggestions || jobData.result.analysis || '');

    const originalText = (jobData.result.original_content || jobData.result.original_data || '').toString();

    // 4) Run AI for the selected upsell
    const startTs = Date.now();

    const runModel = async (prompt) => {
      const model = genAI.getGenerativeModel({
        model: 'gemini-2.5-flash',
        generationConfig: { maxOutputTokens: 32000, temperature: 0.2 }
      });
      const res = await model.generateContent(prompt);
      return (await res.response).text();
    };

    let enhancedContent = '';
    if (offerId === 'executive_summary') {
      const prompt = `Create a concise executive summary with 3-5 bullet points for this content. Return only the bullet list, no intro/outro text.\n\nCONTENT:\n${baseText}`;
      const bullets = await runModel(prompt);
      enhancedContent = `Executive Summary\n${bullets}\n\n${baseText}`.trim();
    } else if (offerId === 'style_themes') {
      const theme = options.theme || 'Modern Corporate';
      const prompt = `Rewrite the document content applying the "${theme}" professional theme. Maintain all meaning. Improve headings hierarchy, emphasize key points, tighten wording, and ensure executive tone. Return the full improved content only.\n\nCONTENT:\n${baseText}`;
      enhancedContent = await runModel(prompt);
    } else if (offerId === 'translation') {
      const target = options.lang || 'es';
      console.log('Translation upsell processing:', { target, baseTextLength: baseText.length });
      const prompt = `Translate the content to ${target} preserving structure, headings, and bullet lists. Return only translated text.\n\nCONTENT:\n${baseText}`;
      enhancedContent = await runModel(prompt);
      console.log('Translation completed, length:', enhancedContent.length);
    } else if (offerId === 'bulk_processing') {
      return { success: false, error: 'Not supported in client yet' };
    } else {
      return { success: false, error: 'Unknown upsell type' };
    }

    const processingTime = Date.now() - startTs;

    // 5) Deduct credits and create a transaction
    await updateDoc(userRef, {
      credits: increment(-offerCredits),
      updatedAt: serverTimestamp()
    });
    try {
      await addDoc(collection(db, 'transactions'), {
        userId,
        transactionType: 'upsell_purchase',
        amount: -offerCredits,
        description: `Upsell applied: ${offerId}`,
        status: 'completed',
        visible: true,
        createdAt: serverTimestamp()
      });
    } catch (_) { }

    // 6) Update job result with upsell artifact
    const resultUpdate = { ...jobData.result };
    resultUpdate.upsells = Array.isArray(resultUpdate.upsells) ? resultUpdate.upsells : [];
    resultUpdate.upsells.push({
      id: offerId,
      creditsUsed: offerCredits,
      processingTime,
      appliedAt: new Date().toISOString()
    });

    // Store the enhanced content in a dedicated field depending on upsell
    if (offerId === 'executive_summary') {
      resultUpdate.enhancement_suggestions = enhancedContent;
      // Also surface summary bullets if present in result
      if (Array.isArray(resultUpdate.improvementSummary)) {
        // Keep existing
      }
    } else if (offerId === 'style_themes') {
      resultUpdate.enhancement_suggestions = enhancedContent;
      resultUpdate.styledTheme = options.theme || 'Modern Corporate';
    } else if (offerId === 'translation') {
      resultUpdate.translated_text = enhancedContent;
      resultUpdate.translation_lang = options.lang || 'es';
      console.log('Translation result updated:', {
        translatedTextLength: enhancedContent.length,
        language: options.lang || 'es',
        resultUpdateKeys: Object.keys(resultUpdate)
      });
    }

    await updateDoc(jobRef, { result: resultUpdate, updatedAt: serverTimestamp() });

    return { success: true, creditsUsed: offerCredits, processingTime, enhancedContent, result: resultUpdate };
  } catch (error) {
    console.error('applyUpsellToJob error:', error);
    return { success: false, error: error.message || 'Upsell failed' };
  }
};

export const getUpsellOffer = (id) => {
  const map = {
    'executive_summary': { credits: 5 },
    'style_themes': { credits: 8 },
    'translation': { credits: 10 },

    'bulk_processing': { credits: 25 },
  };
  return map[id] || null;
};

// Helper to build upsell-specific prompt additions without changing main flow
const buildUpsellPromptAddendum = (preUpsells) => {
  if (!preUpsells) return '';
  const parts = [];
  // Style theme instruction
  if (preUpsells.style_themes) {
    const theme = String(preUpsells.style_themes);
    parts.push(`UPSOLD FEATURE: PROFESSIONAL THEME\n- Apply the "${theme}" professional theme to the improved output.\n- Enforce consistent headings hierarchy, clear typography, and professional tone.\n- Use strong headings, emphasis for key points, and consistent spacing.\n- Do NOT remove content; only improve presentation and tone.`);
  }
  // Translation instruction
  if (preUpsells.translation) {
    const lang = String(preUpsells.translation);
    parts.push(`UPSOLD FEATURE: TRANSLATION\n- After you produce the full improved output in the original language, also produce a FULLY TRANSLATED version in ${lang}.\n- Keep the structure, headings, and bulleting consistent.\n- Separate the translated version clearly under a heading: "TRANSLATED OUTPUT (lang=${lang})".\n- Do NOT omit the original improved output.`);
  }
  return parts.length ? `\n\n=== UPSOLD FEATURES INSTRUCTIONS ===\n${parts.join('\n\n')}\n=== END UPSOLD FEATURES INSTRUCTIONS ===\n` : '';
};